import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Wallet, Sparkles, X, Copy, CheckCircle2, XCircle, Check, ChevronDown,
  ChevronLeft, RefreshCw, UserRound, AlertTriangle, Clock3, Zap,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import SafeImage from '../../../components/SafeImage';
import { resolvePaymentBrandLogo } from '../../../lib/paymentBrands';
import { openExternalUrl } from '../../../lib/externalNavigation';
import { optimizeProofImage, proofUploadErrorMessage } from '../../../lib/proofImageUpload';
import {
  DEFAULT_GATEWAY_AMOUNTS,
  normalizeGatewayAmountOptions,
} from './walletAmountOptions';
import { calculateExpressCashoutBreakdown, effectiveMinimumWithdrawal } from './cashoutAmounts';
import useLatestDepositMethod from './useLatestDepositMethod';

// A manual proof request must survive the wallet sheet closing. Keeping the
// promise outside the component lets the SPA return the player home instantly
// without cancelling the upload when WalletFlow unmounts.
const activeManualDepositJobs = new Map();
const waitForManualRetry = (ms) => new Promise(resolve => window.setTimeout(resolve, ms));
async function runWithBoundedManualRetry(operation) {
  const retryDelays = [750, 1750, 3500];
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if ((status > 0 && status < 500) || attempt >= retryDelays.length) throw error;
      await waitForManualRetry(retryDelays[attempt]);
    }
  }
}

/* -------------------- FULL-SCREEN WHITE PANEL -------------------- */
export function FullScreenWhitePanel({ children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ background: '#FFFFFF', color: '#0B0F0C', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/* -------------------- STEPPER (dynamic 2 or 3 steps) -------------------- */
export function Stepper({ step, accent, accentDark, labels = ['Payment method', 'Pay'] }) {
  const Step = ({ n, label, state }) => {
    const isActive = state === 'active';
    const isDone = state === 'done';
    const ringColor = isActive || isDone ? accent : '#D1D5DB';
    const fillColor = isDone ? accent : 'transparent';
    const numColor = isDone ? '#fff' : (isActive ? accentDark : '#9CA3AF');
    const labelColor = isActive || isDone ? accentDark : '#9CA3AF';
    return (
      <div className="relative z-10 flex min-w-0 flex-col items-center gap-1.5 sm:gap-2">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-extrabold transition-all display sm:h-12 sm:w-12 sm:text-base"
          style={{
            border: `3px solid ${ringColor}`,
            background: fillColor,
            color: numColor,
            boxShadow: isActive ? `0 0 0 6px ${accent}1F` : 'none'
          }}
          data-testid={`stepper-${n}`}
        >
          {isDone ? <Check className="w-5 h-5" strokeWidth={4} /> : n}
        </div>
        <span className="w-full break-words px-0.5 text-center text-[9px] font-bold leading-tight tracking-normal display min-[380px]:text-[10px] sm:text-sm sm:tracking-wide" style={{ color: labelColor }}>
          {label}
        </span>
      </div>
    );
  };
  return (
    <div
      className="mb-2 grid items-start"
      style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}
      data-testid="wallet-stepper"
    >
      {labels.map((label, idx) => {
        const n = idx + 1;
        const state = step > n ? 'done' : step === n ? 'active' : 'idle';
        return (
          <div key={n} className="relative min-w-0">
            {idx < labels.length - 1 && (
              <div
                className="absolute left-1/2 right-[-50%] top-[19px] h-[3px] rounded-full transition-colors sm:top-[22px]"
                style={{ background: step > n ? accent : '#E5E7EB' }}
              />
            )}
            <Step n={n} label={label} state={state} />
          </div>
        );
      })}
    </div>
  );
}

/* -------------------- ROW (small label/value pair) -------------------- */
export function Row({ label, value, valueBold, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="uppercase tracking-wider text-xs font-bold" style={{ color: '#6B7280' }}>{label}</span>
      <span
        className={`text-right ${mono ? 'mono' : ''} ${valueBold ? 'display font-extrabold' : 'font-medium'}`}
        style={{ color: '#0B0F0C', wordBreak: 'break-word', maxWidth: '60%' }}
      >
        {value}
      </span>
    </div>
  );
}

/* -------------------- PAYMENT METHOD LOGO --------------------
   Renders an authentic-looking brand mark for the supported gateway
   methods (cashapp / applepay / googlepay), and falls back to a
   generic Wallet icon for tenant-custom manual methods.
*/
export function PaymentMethodLogo({ method, accent, accentDark, size = 'md' }) {
  const code = (method?.gateway_method_code || method?.method || method?.gateway_method || '').toLowerCase();
  const dim = size === 'sm' ? 44 : 48;
  const iconPx = size === 'sm' ? 22 : 26;
  const rawLogoUrl = String(method?.logo_url || '').trim();
  const brandLogo = resolvePaymentBrandLogo(method);
  const resolvedLogoUrl = rawLogoUrl
    ? (rawLogoUrl.startsWith('http://') || rawLogoUrl.startsWith('https://')
        ? rawLogoUrl
        : rawLogoUrl.startsWith('/payment-logos/')
          ? rawLogoUrl
        : `${process.env.REACT_APP_BACKEND_URL || ''}${rawLogoUrl}`)
    : (brandLogo || '');

  if (resolvedLogoUrl) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
        style={{
          width: dim,
          height: dim,
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.10)',
          boxShadow: '0 4px 10px rgba(0,0,0,0.10)',
        }}
        aria-hidden
      >
        <SafeImage
          src={resolvedLogoUrl}
          alt={method?.title || method?.method || 'Payment method'}
          className="w-full h-full object-contain"
          loading="lazy"
          fallback={<Wallet className="w-6 h-6" style={{ color: accentDark || accent || '#15803D' }} />}
        />
      </span>
    );
  }

  if (code === 'cashapp' || code.includes('cash')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#00D632', boxShadow: '0 4px 10px rgba(0,214,50,0.4)' }}
        aria-hidden
      >
        <svg viewBox="0 0 24 24" width={iconPx} height={iconPx}>
          <path fill="#FFFFFF" d="M19.6 4.4C18.5 3.3 17.1 3 15.7 3H8.3c-1.4 0-2.8.3-3.9 1.4C3.3 5.5 3 6.9 3 8.3v7.4c0 1.4.3 2.8 1.4 3.9 1.1 1.1 2.5 1.4 3.9 1.4h7.4c1.4 0 2.8-.3 3.9-1.4 1.1-1.1 1.4-2.5 1.4-3.9V8.3c0-1.4-.3-2.8-1.4-3.9zM14.2 9.6c-.3-.3-.7-.6-1.4-.7l-.1-1c0-.2-.1-.3-.3-.3h-.7c-.2 0-.3.1-.3.3l-.1.9c-1.4.1-2.5.9-2.5 2.3 0 1.2.8 1.8 2 2.2.9.3 1.4.5 1.4 1 0 .4-.4.7-1.1.7-.7 0-1.4-.3-1.9-.7-.1-.1-.3-.1-.4 0l-.7.7c-.1.1-.1.3 0 .4.5.4 1.2.8 2 1l-.1 1c0 .2.1.3.3.3h.7c.2 0 .3-.1.3-.3l.1-1c1.4-.2 2.4-1 2.4-2.4 0-1.2-.8-1.8-2.2-2.3-.9-.3-1.3-.5-1.3-.9s.4-.6 1-.6c.6 0 1.2.2 1.7.6.1.1.3.1.4 0l.7-.7c.1-.1.1-.3 0-.4z"/>
        </svg>
      </span>
    );
  }

  if (code === 'applepay' || code === 'apple_pay' || code.includes('apple')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#000000', boxShadow: '0 4px 10px rgba(0,0,0,0.4)' }}
        aria-hidden
      >
        <svg viewBox="0 0 60 24" width={iconPx + 14} height={iconPx}>
          <path fill="#FFFFFF" d="M11.55 4.36a3.18 3.18 0 0 0-.74 2.39 3.07 3.07 0 0 0 2.34-1.21 3.36 3.36 0 0 0 .79-2.41 3.4 3.4 0 0 0-2.39 1.23zm2.34 1.85c-1.29-.07-2.39.73-3 .73s-1.56-.7-2.58-.68a3.84 3.84 0 0 0-3.27 2C3.69 11 5 16.05 6.46 18.81c.74 1.36 1.61 2.86 2.76 2.82s1.51-.71 2.84-.71 1.7.71 2.84.69 1.93-1.36 2.65-2.72a11.94 11.94 0 0 0 1.21-2.51 3.92 3.92 0 0 1-2.36-3.57 4 4 0 0 1 1.91-3.34 4.18 4.18 0 0 0-3.27-1.76M27.7 4.7v15.7h2.4v-5.32h3.32c3.05 0 5.18-2.06 5.18-5.2 0-3.13-2.1-5.18-5.1-5.18zM30.1 6.8h2.74c2.06 0 3.24 1.1 3.24 3.06s-1.18 3.08-3.25 3.08H30.1zm12.81 13.83c1.7 0 3.16-.84 3.85-2.18h.05v2.05h2.21v-7.7c0-2.21-1.79-3.65-4.55-3.65-2.56 0-4.46 1.46-4.5 3.46h2.16c.18-1 1.07-1.62 2.27-1.62 1.46 0 2.31.71 2.31 2v.92l-3.07.18c-2.7.16-4.16 1.36-4.16 3.4 0 2.06 1.55 3.14 3.43 3.14zm.65-1.84c-1.27 0-2.07-.61-2.07-1.55 0-.97.78-1.53 2.27-1.62l2.74-.18v.95c0 1.45-1.23 2.4-2.94 2.4zM52.27 24c2.32 0 3.41-.92 4.36-3.69l4.18-12.34h-2.43L55.55 17.6h-.05L52.7 7.97h-2.51l4.05 11.71-.22.71c-.36 1.18-.96 1.65-2.06 1.65-.2 0-.59-.02-.74-.04v1.86c.15.04.78.05.91.05z"/>
        </svg>
      </span>
    );
  }

  if (code === 'googlepay' || code === 'google_pay' || code.includes('google')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.12)', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
        aria-hidden
      >
        <svg viewBox="0 0 56 24" width={iconPx + 14} height={iconPx}>
          <path fill="#5F6368" d="M26.54 11.66v4.93h-1.56V4.51h4.18a3.78 3.78 0 0 1 2.71 1.06 3.45 3.45 0 0 1 0 5.07 3.81 3.81 0 0 1-2.71 1.02zm0-5.66v4.16h2.66a2.4 2.4 0 0 0 1.79-.71 2.34 2.34 0 0 0 0-3.32 2.4 2.4 0 0 0-1.79-.7zM37 8.14a4.7 4.7 0 0 1 3.32 1.16 3.96 3.96 0 0 1 1.21 3.05v6.16h-1.49v-1.16h-.07c-.65 1-1.78 1.6-2.97 1.56a3.85 3.85 0 0 1-2.66-.94 3.04 3.04 0 0 1-1.05-2.34c0-.99.37-1.78 1.12-2.36a4.66 4.66 0 0 1 2.97-.86 5.15 5.15 0 0 1 2.62.58v-.36a2.05 2.05 0 0 0-.71-1.56 2.45 2.45 0 0 0-1.66-.66 2.6 2.6 0 0 0-2.27 1.21l-1.36-.86A4.04 4.04 0 0 1 37 8.14m-2 6.05c0 .43.21.84.55 1.09.39.31.87.46 1.36.45a2.79 2.79 0 0 0 1.97-.81 2.61 2.61 0 0 0 .87-1.92 3.65 3.65 0 0 0-2.32-.66c-.71 0-1.31.18-1.79.51-.43.32-.65.74-.65 1.34M52 8.41 46.79 20.36h-1.61l1.95-4.2-3.43-7.75h1.7l2.49 6h.04l2.42-6z"/>
          <path fill="#4285F4" d="M19.36 12.65a8.37 8.37 0 0 0-.13-1.5h-7v2.83h4a3.42 3.42 0 0 1-1.49 2.25v1.85h2.4c1.4-1.3 2.22-3.21 2.22-5.43z"/>
          <path fill="#34A853" d="M12.23 19.92c2 0 3.69-.66 4.91-1.79l-2.4-1.85c-.67.45-1.52.71-2.51.71-1.92 0-3.55-1.3-4.13-3.04H5.61v1.91a7.4 7.4 0 0 0 6.62 4.06z"/>
          <path fill="#FBBC04" d="M8.1 13.95a4.43 4.43 0 0 1 0-2.83V9.21H5.61a7.4 7.4 0 0 0 0 6.65z"/>
          <path fill="#EA4335" d="M12.23 8.07a4.01 4.01 0 0 1 2.83 1.11l2.12-2.13a7.13 7.13 0 0 0-4.95-1.93 7.4 7.4 0 0 0-6.62 4.07L8.1 11.1c.58-1.74 2.21-3.04 4.13-3.04z"/>
        </svg>
      </span>
    );
  }

  return (
    <span
      className="rounded-xl flex items-center justify-center shrink-0"
      style={{
        width: dim, height: dim,
        background: `${accent}1A`,
        border: `1px solid ${accent}55`,
      }}
      aria-hidden
    >
      <Wallet style={{ width: iconPx, height: iconPx, color: accentDark }} />
    </span>
  );
}

const gatewayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const gatewaySubmethodCode = (method) => String(
  method?.gateway_submethod
  || method?.method
  || method?.gateway_method
  || method?.gateway_method_code
  || method?.code
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const isManualCashappQrMethod = (method) => {
  if (!method) return false;
  if (String(method.payment_mode || '').toUpperCase() === 'GATEWAY') return false;
  const compact = [
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return compact.includes('cashappqr');
};

const canonicalApiGatewayCode = (method) => {
  const compact = [
    method?.gateway_submethod,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
    method?.receiving_address,
    method?.method_details,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (isManualCashappQrMethod(method)) return 'cashapp_qr';
  if (
    compact.includes('cashappvialt')
    || compact.includes('cashapplt')
    || compact.includes('ecashapp')
    || compact.includes('cashappvialightning')
    || compact.includes('cashappviaspeed')
  ) return 'cashapp_via_lt';
  if (compact.includes('cashapp')) return 'cashapp';
  if (compact.includes('applepay')) return 'applepay';
  if (compact.includes('googlepay') || compact.includes('gpay')) return 'googlepay';
  if (compact.includes('chime')) return 'chime';
  if (compact.includes('venmo')) return 'venmo';
  if (compact.includes('nowpayments') || compact.includes('crypto') || compact.includes('usdt')) return 'crypto';
  return gatewayCode(method);
};

const canonicalPlayerGatewayKey = (method) => {
  const compact = [
    method?.gateway_submethod,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
    method?.gateway_provider,
    method?.checkout_link_provider_code,
    method?.provider_code,
    method?.base_url,
    method?.api_url,
    method?.checkout_url,
    method?.receiving_address,
    method?.method_details,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (
    compact.includes('cashappvialt')
    || compact.includes('cashapplt')
    || compact.includes('ecashapp')
    || compact.includes('cashappvialightning')
    || compact.includes('cashappviaspeed')
  ) return 'cashapp_via_lt';
  if (compact.includes('cashapp')) return 'cashapp';
  if (compact.includes('applepay')) return 'applepay';
  if (compact.includes('googlepay') || compact.includes('gpay')) return 'googlepay';
  if (compact.includes('chime')) return 'chime';
  if (compact.includes('venmo')) return 'venmo';
  if (
    compact.includes('nowpayments')
    || compact.includes('crypto')
    || compact.includes('usdt')
    || compact.includes('bitcoin')
    || compact.includes('btc')
  ) return 'crypto';
  return canonicalApiGatewayCode(method);
};

const paymentMethodBadgeLabel = (method, mode) => {
  const custom = String(
    method?.player_badge_label
    || method?.player_badge
    || method?.badge_label
    || method?.tag_label
    || method?.display_tag
    || ''
  ).trim();
  if (custom) return custom;
  const bonus = Number(method?.bonus_percent || method?.bonus_or_fee || 0);
  if (mode === 'deposit' && bonus > 0 && (method?.bonus_fee_direction || 'bonus') === 'bonus') {
    return `+${bonus}% BONUS`;
  }
  return '';
};

const paymentBadgeStyle = (tone = 'gold', accent = '#F59E0B') => {
  const styles = {
    gold: {
      color: '#7A4A00',
      borderColor: 'rgba(245, 158, 11, 0.48)',
      background: 'linear-gradient(135deg, rgba(255,237,135,0.98), rgba(245,158,11,0.95))',
      boxShadow: '0 0 14px rgba(245, 158, 11, 0.32)',
    },
    green: {
      color: '#065F46',
      borderColor: 'rgba(16,185,129,0.42)',
      background: 'linear-gradient(135deg, rgba(209,250,229,0.98), rgba(52,211,153,0.95))',
      boxShadow: '0 0 14px rgba(16,185,129,0.28)',
    },
    blue: {
      color: '#075985',
      borderColor: 'rgba(56,189,248,0.42)',
      background: 'linear-gradient(135deg, rgba(224,242,254,0.98), rgba(56,189,248,0.92))',
      boxShadow: '0 0 14px rgba(56,189,248,0.28)',
    },
    purple: {
      color: '#4C1D95',
      borderColor: 'rgba(168,85,247,0.42)',
      background: 'linear-gradient(135deg, rgba(243,232,255,0.98), rgba(168,85,247,0.88))',
      boxShadow: '0 0 14px rgba(168,85,247,0.26)',
    },
    red: {
      color: '#881337',
      borderColor: 'rgba(244,63,94,0.42)',
      background: 'linear-gradient(135deg, rgba(255,228,230,0.98), rgba(244,63,94,0.88))',
      boxShadow: '0 0 14px rgba(244,63,94,0.24)',
    },
  };
  return styles[tone] || {
    color: '#111827',
    borderColor: `${accent}55`,
    background: `linear-gradient(135deg, #FFFFFF, ${accent}33)`,
    boxShadow: `0 0 14px ${accent}28`,
  };
};

const cashoutMethodLabels = (method) => {
  const configuredFields = Array.isArray(method?.payout_fields)
    ? method.payout_fields.filter(field => field && typeof field === 'object')
    : [];
  const configuredNameField = configuredFields[0] || null;
  const configuredTagField = configuredFields[1] || null;
  const compact = [
    method?.gateway_submethod,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
    method?.receiving_address,
    method?.method_details,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const withConfiguredFields = (fallback) => ({
    ...fallback,
    nameLabel: String(configuredNameField?.label || fallback.nameLabel).trim(),
    tagLabel: String(configuredTagField?.label || fallback.tagLabel).trim(),
    namePlaceholder: String(configuredNameField?.placeholder || configuredNameField?.label || fallback.namePlaceholder).trim(),
    tagPlaceholder: String(configuredTagField?.placeholder || configuredTagField?.label || fallback.tagPlaceholder).trim(),
  });
  if (compact.includes('cashapp')) {
    return withConfiguredFields({
      methodName: 'Cash App',
      nameLabel: 'Cash App Name',
      tagLabel: 'Cashtag',
      namePlaceholder: 'Your Cash App name',
      tagPlaceholder: 'Your Cashtag',
    });
  }
  if (compact.includes('chime')) {
    return withConfiguredFields({
      methodName: 'Chime',
      nameLabel: 'Chime Name',
      tagLabel: 'Chime Tag',
      namePlaceholder: 'Your Chime name',
      tagPlaceholder: 'Your Chime tag',
    });
  }
  const rawTitle = String(method?.title || method?.display_name || method?.name || 'Cashout').trim();
  const methodName = rawTitle || 'Cashout';
  return withConfiguredFields({
    methodName,
    nameLabel: `${methodName} Name`,
    tagLabel: `${methodName} Tag`,
    namePlaceholder: `Your ${methodName} name`,
    tagPlaceholder: `Your ${methodName} tag`,
  });
};

const isApplePayGateway = (method) => {
  const values = [
    method?.gateway_submethod,
    method?.method,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
  ];
  return values.some(value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '').includes('applepay'));
};

const isInternalThreeInOne = (method) => {
  const code = String(method?.method || method?.code || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return code === '3in1' || code === 'threeinone' || code === 'weblink';
};

export const EXPRESS_PAYOUT_METHODS = [
  {
    id: 'express-ecashapp',
    title: 'Cash App',
    method: 'cashapp',
    express_way_code: 'ecashapp',
    description: 'Send to your exact Cashtag',
    primaryLabel: 'Cashtag',
    primaryPlaceholder: '$username',
    player_enabled: false,
  },
  {
    id: 'express-paypal',
    title: 'PayPal',
    method: 'paypal',
    express_way_code: 'paypal',
    description: 'Send to your PayPal email',
    primaryLabel: 'PayPal email',
    primaryPlaceholder: 'you@example.com',
    player_enabled: false,
  },
  {
    id: 'express-venmo',
    title: 'Venmo',
    method: 'venmo',
    express_way_code: 'venmo',
    description: 'Send to your Venmo email',
    primaryLabel: 'Venmo email',
    primaryPlaceholder: 'you@example.com',
    player_enabled: false,
  },
  {
    id: 'express-chime',
    title: 'Chime',
    method: 'chime',
    express_way_code: 'chime',
    description: 'Send to your Chime sign',
    primaryLabel: 'Chime recipient',
    primaryPlaceholder: 'Email, phone or Chime sign',
    player_enabled: false,
  },
  {
    id: 'express-zelle',
    title: 'Zelle',
    method: 'zelle',
    express_way_code: 'zelle',
    description: 'Send to your Zelle recipient',
    primaryLabel: 'Zelle recipient',
    primaryPlaceholder: 'Email or phone number',
    player_enabled: false,
  },
  {
    id: 'express-card',
    title: 'Debit Card',
    method: 'card',
    express_way_code: 'card',
    description: 'Send to an eligible debit card',
    primaryLabel: 'Card number',
    primaryPlaceholder: 'Enter your card number',
    secondaryLabel: 'Card expiry',
    secondaryPlaceholder: 'MM/YY',
    player_enabled: true,
  },
  {
    id: 'express-ach',
    title: 'Bank / ACH',
    method: 'ach',
    express_way_code: 'ach',
    description: 'Send to your bank account',
    primaryLabel: 'Account number',
    primaryPlaceholder: '4–17 digit account number',
    secondaryLabel: 'Routing number',
    secondaryPlaceholder: '9 digit routing number',
    player_enabled: false,
  },
];

const EXPRESS_WAY_CODES = new Set(EXPRESS_PAYOUT_METHODS.map(item => item.express_way_code));
const PLAYER_VISIBLE_EXPRESS_PAYOUT_METHODS = EXPRESS_PAYOUT_METHODS.filter(item => item.player_enabled === true);

export const formatCardNumberInput = (value) => String(value || '').replace(/\D/g, '').slice(0, 16);

export const formatCardExpiryInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const normalizeCardExpiryForProvider = (value) => {
  const formatted = formatCardExpiryInput(value);
  const match = formatted.match(/^((?:0[1-9])|(?:1[0-2]))\/(\d{2}|\d{4})$/);
  if (!match) return formatted;
  const [, month, year] = match;
  return `${month}/${year.length === 2 ? `20${year}` : year}`;
};

export const expressPayoutValidationError = (wayCode, primaryValue, secondaryValue = '') => {
  const primary = String(primaryValue || '').trim();
  const secondary = String(secondaryValue || '').trim();
  if (wayCode === 'ecashapp') {
    const cashtag = primary.startsWith('$') ? primary : `$${primary}`;
    return /^\$[A-Za-z0-9_.-]{3,20}$/.test(cashtag) ? '' : 'Enter a valid Cash App Cashtag';
  }
  if (wayCode === 'paypal' || wayCode === 'venmo') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primary) ? '' : `Enter a valid ${wayCode === 'paypal' ? 'PayPal' : 'Venmo'} email`;
  }
  if (wayCode === 'card') {
    if (!/^\d{12,16}$/.test(primary.replace(/\D/g, ''))) return 'Card number must contain 12–16 digits';
    return /^(0[1-9]|1[0-2])\/(?:\d{2}|\d{4})$/.test(secondary) ? '' : 'Card expiry must use MM/YY';
  }
  if (wayCode === 'ach') {
    if (!/^\d{4,17}$/.test(primary.replace(/\D/g, ''))) return 'Bank account number must contain 4–17 digits';
    return /^\d{9}$/.test(secondary.replace(/\D/g, '')) ? '' : 'Routing number must contain exactly 9 digits';
  }
  if (wayCode === 'chime' || wayCode === 'zelle') {
    return primary && primary.length <= 160 ? '' : `Enter a valid ${wayCode === 'chime' ? 'Chime' : 'Zelle'} recipient`;
  }
  return 'Select an Express payout method';
};

export const buildExpressPayoutDetails = (wayCode, primaryValue, secondaryValue = '') => {
  const primary = String(primaryValue || '').trim();
  const secondary = String(secondaryValue || '').trim();
  if (wayCode === 'ecashapp') return { wayCode, cashtag: primary.startsWith('$') ? primary : `$${primary}` };
  if (wayCode === 'paypal' || wayCode === 'venmo') return { wayCode, email: primary.toLowerCase() };
  if (wayCode === 'card') return {
    wayCode,
    cardNumber: primary.replace(/\D/g, ''),
    cardValid: normalizeCardExpiryForProvider(secondary),
  };
  if (wayCode === 'ach') return { wayCode, accountNumber: primary.replace(/\D/g, ''), routingNumber: secondary.replace(/\D/g, '') };
  if (wayCode === 'chime') return { wayCode, chimeSign: primary };
  if (wayCode === 'zelle') return { wayCode, zelleSign: primary };
  return { wayCode };
};

const API_GATEWAY_CODES = new Set(['cashapp', 'cashapp_via_lt', 'applepay', 'googlepay', 'chime', 'venmo', 'crypto', 'nowpayments']);
const CRYPTO_MIN_AMOUNT = 20;
const CASHAPP_QR_MIN_AMOUNT = 20;
const DEFAULT_CRYPTO_NETWORKS = [
  { code: 'usdttrc20', label: 'USDT TRC20' },
  { code: 'ltc', label: 'LTC' },
  { code: 'btc', label: 'BTC' },
  { code: 'sol', label: 'SOL' },
];
const normalizeCryptoNetwork = (value) => {
  const compact = String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (!compact) return '';
  if (['usdttrc20', 'trc20', 'tethertrc20', 'usdtontron'].includes(compact)) return 'usdttrc20';
  if (['usdterc20', 'erc20', 'tethererc20'].includes(compact)) return 'usdterc20';
  if (['btc', 'bitcoin'].includes(compact)) return 'btc';
  if (['ltc', 'litecoin'].includes(compact)) return 'ltc';
  if (['sol', 'solana'].includes(compact)) return 'sol';
  if (['eth', 'ethereum'].includes(compact)) return 'eth';
  if (['doge', 'dogecoin'].includes(compact)) return 'doge';
  return compact;
};

const cryptoNetworkLabel = (code, fallback = '') => {
  const normalized = normalizeCryptoNetwork(code);
  const defaultOption = DEFAULT_CRYPTO_NETWORKS.find(option => option.code === normalized);
  if (defaultOption) return defaultOption.label;
  if (normalized === 'usdterc20') return 'USDT ERC20';
  if (normalized === 'eth') return 'ETH';
  if (normalized === 'doge') return 'DOGE';
  return fallback || String(code || '').toUpperCase();
};

const cryptoPayAmountLabel = (amount, currency) => {
  const amountText = String(amount ?? '').trim();
  const currencyText = String(currency || '').trim().toUpperCase();
  if (!amountText) return currencyText || '-';
  return `${amountText}${currencyText ? ` ${currencyText}` : ''}`;
};

const cryptoNetworkOptionsFor = (method) => {
  const raw = (
    Array.isArray(method?.crypto_networks) && method.crypto_networks.length ? method.crypto_networks
      : Array.isArray(method?.allowed_crypto_networks) && method.allowed_crypto_networks.length ? method.allowed_crypto_networks
        : Array.isArray(method?.payment_networks) && method.payment_networks.length ? method.payment_networks
          : []
  );
  const source = raw.length ? raw : DEFAULT_CRYPTO_NETWORKS;
  const options = source
    .map(item => {
      if (typeof item === 'string') {
        const code = normalizeCryptoNetwork(item);
        return code ? { code, label: cryptoNetworkLabel(code, item) } : null;
      }
      const code = normalizeCryptoNetwork(item?.code || item?.value || item?.network || item?.currency || item?.id || item?.label);
      if (!code) return null;
      return { code, label: item?.label || item?.name || cryptoNetworkLabel(code) };
    })
    .filter(Boolean)
    .filter((item, idx, arr) => arr.findIndex(option => option.code === item.code) === idx);
  return options.length ? options : DEFAULT_CRYPTO_NETWORKS;
};

const isCryptoGatewayMethod = (method) => canonicalApiGatewayCode(method) === 'crypto';
const isECashappGatewayMethod = (method) => canonicalApiGatewayCode(method) === 'cashapp_via_lt';

const isApiGatewayMethod = (method) => {
  if (!method) return false;
  if ((method.payment_mode || '').toUpperCase() === 'GATEWAY') return true;

  const provider = String(
    method.gateway_provider
    || method.checkout_link_provider_code
    || method.provider_code
    || ''
  ).toLowerCase();
  if (provider && !['manual', 'local_manual', 'local'].includes(provider)) return true;

  const code = canonicalApiGatewayCode(method);
  if (!API_GATEWAY_CODES.has(code)) return false;
  if (code === 'cashapp_via_lt' || code === 'crypto') return true;

  const gatewayHints = [
    method.gateway_method,
    method.gateway_method_code,
    method.gateway_submethod,
    method.base_url,
    method.api_url,
    method.checkout_url,
    method.receiving_address,
    method.method_details,
    method.tags,
  ].join(' ').toLowerCase();

  return (
    gatewayHints.includes('www.automationemp.com')
    || gatewayHints.includes('1clickpay')
    || gatewayHints.includes('linkgen')
    || gatewayHints.includes('nowpayments')
    || gatewayHints.includes('paymentapi.vegaslobby.vip')
  );
};

const iframeCheckoutUrl = (payUrl, applePay) => {
  try {
    const parsed = new URL(payUrl);
    if (parsed.hostname.endsWith('nowpayments.io')) {
      parsed.protocol = 'https:';
      if (parsed.pathname === '/payment/') parsed.pathname = '/payment';
      return parsed.toString();
    }
    if (!applePay) return payUrl;
    if (parsed.hostname === 'checkout.nexmindglobal.com') {
      return `${window.location.origin}/__applepay-checkout${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Fall back to the gateway URL if the browser cannot parse it.
  }
  return payUrl;
};

const shouldUseExternalPaymentLink = (method) => {
  const code = canonicalApiGatewayCode(method);
  if (isECashappGatewayMethod(method)) return true;
  if (code === 'cashapp' || code === 'cashapp_via_lt' || code === 'applepay' || code === 'googlepay' || code === 'chime' || code === 'venmo' || code === 'crypto') {
    return true;
  }
  const mode = String(
    method?.player_checkout_mode
    || method?.checkout_mode
    || method?.checkout_presentation
    || ''
  ).toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (['externallink', 'paymentlink', 'website', 'link', 'browser'].includes(mode)) {
    return true;
  }
  if (['iframe', 'embed', 'embedded', 'inline'].includes(mode)) {
    return false;
  }
  return false;
};

const openPaymentLinkExternally = (url) => {
  openExternalUrl(url);
};


const amountOptionsFor = (method) => {
  if (isCryptoGatewayMethod(method)) return [];
  const canonicalCode = canonicalApiGatewayCode(method);
  const hasAllowedAmounts = Array.isArray(method?.allowed_amounts);
  const hasPresetAmounts = Array.isArray(method?.preset_amounts);
  const configured = (
    hasAllowedAmounts
      ? method.allowed_amounts
      : hasPresetAmounts
        ? method.preset_amounts
        : []
  );
  const gatewayDefaults = isApiGatewayMethod(method)
    ? DEFAULT_GATEWAY_AMOUNTS[canonicalCode] || []
    : [];
  const raw = hasAllowedAmounts || hasPresetAmounts ? configured : gatewayDefaults;
  return normalizeGatewayAmountOptions(canonicalCode, raw);
};

const hasExplicitGatewayAmountOptions = (method) => (
  Array.isArray(method?.allowed_amounts) || Array.isArray(method?.preset_amounts)
);

const paymentMethodDisplayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || method?.title
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const isCashappQrMethod = (method) => {
  const code = paymentMethodDisplayCode(method);
  return code === 'cashappqr' || code === 'cashappqrupload';
};

const isCashappOrChimePaymentMethod = (method = {}) => {
  const compact = [
    method.method,
    method.code,
    method.title,
    method.name,
    method.display_name,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return compact.includes('cashapp') || compact.includes('chime');
};

const isChimeAutoPaymentMethod = (method = {}) => {
  const compact = [
    method.method,
    method.code,
    method.title,
    method.name,
    method.display_name,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return compact.includes('chimeauto');
};

const minAmountForDepositMethod = (method) => {
  if (isCashappQrMethod(method)) {
    return Math.max(CASHAPP_QR_MIN_AMOUNT, Number(method?.min_amount || 0) || 0);
  }
  if (isCryptoGatewayMethod(method)) {
    return Math.max(CRYPTO_MIN_AMOUNT, Number(method?.min_amount || 0) || 0);
  }
  if (isECashappGatewayMethod(method)) {
    return Math.max(4.99, Number(method?.min_amount || 0) || 0);
  }
  return Number(method?.min_amount || 0) || 0;
};

const depositMinimumMessage = (method, minAmount) => (
  isCashappQrMethod(method)
    ? `Cashapp QR minimum is $${minAmount}. For loads below $${minAmount}, use CashApp via Speed or Cash App via link.`
    : `Minimum amount for ${method?.title || 'this method'} is $${minAmount}`
);

const defaultPaymentMethodOrder = (method) => {
  const code = paymentMethodDisplayCode(method);
  if (code === 'cashappvialt' || code === 'cashapplt' || code === 'ecashapp') return 1;
  if (code === 'cashappqr') return 1;
  if (code === 'cashapp') return 10;
  if (code === 'applepay') return 20;
  if (code === 'googlepay') return 30;
  if (code === 'chime' && String(method?.title || '').toLowerCase().includes('auto')) return -10;
  if (code === 'chime') return 40;
  if (code === 'venmo') return 50;
  if (code === 'crypto' || code === 'usdt' || code === 'btc' || code === 'bitcoin') return 60;
  return 500;
};

const paymentMethodOrder = (method) => {
  const raw = Number(method?.display_order ?? method?.sort_order ?? method?.order);
  return Number.isFinite(raw) ? raw : defaultPaymentMethodOrder(method);
};

const sortPaymentMethodsForPlayer = (items = []) => [...items].sort((a, b) => {
  const orderDiff = paymentMethodOrder(a) - paymentMethodOrder(b);
  if (orderDiff !== 0) return orderDiff;
  const titleA = String(a?.title || a?.method || '').toLowerCase();
  const titleB = String(b?.title || b?.method || '').toLowerCase();
  return titleA.localeCompare(titleB);
});

const dedupePlayerGatewayMethods = (items = []) => {
  const selected = new Map();
  const passthrough = [];
  const score = (method) => {
    const id = String(method?.id || '');
    return [
      id.startsWith('gateway-') ? 0 : 1,
      method?.logo_url || method?.qr_code_url || method?.qr_image_url ? 1 : 0,
      method?.player_badge_label || method?.tag_label || method?.display_tag ? 1 : 0,
      Array.isArray(method?.allowed_amounts) || Array.isArray(method?.preset_amounts) ? 1 : 0,
    ].join('');
  };

  items.forEach((method) => {
    if (!isApiGatewayMethod(method)) {
      passthrough.push(method);
      return;
    }
    const code = canonicalPlayerGatewayKey(method);
    const key = API_GATEWAY_CODES.has(code) ? code : '';
    if (!key) {
      passthrough.push(method);
      return;
    }
    const current = selected.get(key);
    if (!current || score(method) > score(current)) {
      selected.set(key, method);
    }
  });

  return sortPaymentMethodsForPlayer([...passthrough, ...selected.values()]);
};

function ManualPaymentDetails({ method, accent = '#22C55E', accentDark = '#15803D', showQr = true, beforeQr = null, onPreviewQr = null }) {
  if (!method) return null;
  const details = method.method_details || '';
  const address = String(method.receiving_address || '').trim();
  const tags = String(method.tags || '').trim();
  const note = method.admin_note || '';
  const qr = method.qr_image_url || method.qr_code_url || '';
  const walletName = address || 'Wallet tag name';
  const walletTag = tags || address;
  const noteLines = [details, note].filter(Boolean);
  const canPreviewQr = typeof onPreviewQr === 'function' && isCashappOrChimePaymentMethod(method);
  const supportOnlyMethod = [
    method.title,
    method.method,
    method.player_badge_label,
    details,
    address,
    tags,
    note,
  ].some((value) => String(value || '').toLowerCase().includes('contact support'));
  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (!details && !address && !tags && !note && !(showQr && qr)) return null;

  return (
    <div className="space-y-3">
      {walletTag && (
        <div
          className="rounded-2xl p-4"
          style={{ background: `${accent}10`, border: `1px solid ${accent}55` }}
          data-testid="manual-wallet-tag-card"
        >
          <p className="text-xs font-extrabold break-all" style={{ color: '#0B0F0C' }}>
            {walletName}
          </p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xl font-extrabold mono break-all leading-tight" style={{ color: '#0B0F0C' }}>
              {walletTag}
            </p>
            <button
              type="button"
              onClick={() => copyText(walletTag)}
              className="shrink-0 p-2.5 rounded-xl"
              style={{ background: accent, color: '#fff' }}
              aria-label={`Copy ${walletName} tag`}
              data-testid="copy-manual-wallet-tag"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {!supportOnlyMethod && (
            <p className="mt-2 flex items-start gap-1.5 text-[10px] sm:text-[11px] font-bold leading-snug" style={{ color: '#DC2626' }}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Make sure to double check the tag before sending payment. Payment won&apos;t be loaded if you send on another tag.</span>
            </p>
          )}
        </div>
      )}
      {beforeQr}
      {showQr && qr && (
        <div className="flex justify-center">
          {canPreviewQr ? (
            <button
              type="button"
              onClick={() => onPreviewQr(qr, method.title || 'Payment QR')}
              className="rounded-xl bg-white focus:outline-none focus:ring-4"
              style={{ border: '1px solid rgba(0,0,0,0.1)', '--tw-ring-color': `${accent}66` }}
              aria-label={`Open ${method.title || 'payment'} QR`}
              data-testid="open-manual-payment-qr"
            >
              <SafeImage src={qr} alt={`${method.title || 'Payment'} QR`} className="w-44 h-44 rounded-xl bg-white object-contain" loading="lazy" fallback={null} />
            </button>
          ) : (
            <SafeImage src={qr} alt={`${method.title || 'Payment'} QR`} className="w-44 h-44 rounded-xl bg-white object-contain" style={{ border: '1px solid rgba(0,0,0,0.1)' }} loading="lazy" fallback={null} />
          )}
        </div>
      )}
      {noteLines.length > 0 && (
        <div className="rounded-2xl p-4 text-sm" style={{ background: '#FFFBEB', border: '1px solid rgba(245,158,11,0.35)', color: '#92400E' }}>
          {noteLines.map((line, index) => (
            <p key={`${line}-${index}`} className={index ? 'mt-2' : ''}>{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

const isSupportContactMethod = (method) => {
  if (!method) return false;
  if (isManualCashappQrMethod(method)) return false;
  if (isApiGatewayMethod(method)) return false;
  const hasWalletDetails = String(method.receiving_address || method.tags || '').trim().length > 0;
  const hasQr = String(method.qr_image_url || method.qr_code_url || '').trim().length > 0;
  return !hasWalletDetails && !hasQr;
};

/* -------------------- WALLET FLOW (full-screen, multi-step) -------------------- */
export default function WalletFlow({ mode, cashoutSpeed = 'normal', setMode, step, setStep, method, setMethod, wallet, walletRules = {}, methods, refresh, checkAuth, close, onBackToCashoutChoice, branding = {}, onOpenSupport }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [cryptoNetwork, setCryptoNetwork] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [expressSecondary, setExpressSecondary] = useState('');
  const [payoutName, setPayoutName] = useState('');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [tipAgent, setTipAgent] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [cashoutDetailsWarningOpen, setCashoutDetailsWarningOpen] = useState(false);
  const [checkoutPanel, setCheckoutPanel] = useState(null);
  const [latestSelectedMethod, setLatestSelectedMethod] = useState(null);
  const [manualQrPreview, setManualQrPreview] = useState(null);
  const proofInputRef = useRef(null);
  const proofSelectionRef = useRef(0);
  const proofOptimizationPromiseRef = useRef(Promise.resolve(null));
  const pendingManualDepositRef = useRef(null);
  const submitLockRef = useRef(false);
  // setMode is reserved by parent — referenced here so eslint doesn't trip
  void setMode;

  const normalizedMode = mode === 'withdrawal' ? 'withdraw' : mode;
  const sortedMethods = useMemo(() => dedupePlayerGatewayMethods(methods), [methods]);
  const filteredMethods = sortedMethods.filter(m => {
    const flow = String(m?.flow || '').toLowerCase();
    return (flow === 'both' || flow === normalizedMode) && !isInternalThreeInOne(m);
  });
  const depositRules = useLatestDepositMethod({
    enabled: mode === 'deposit' && step >= 2 && Boolean(method?.id),
    methodId: method?.id,
    playerId: user?.id,
    tenantId: user?.tenant_id,
    step,
  });
  const depositRulesLocked = mode === 'deposit' && step >= 2 && depositRules.status !== 'ready';
  const activeMethod = mode === 'deposit'
    ? (depositRules.method || method)
    : latestSelectedMethod
    && method?.id
    && String(latestSelectedMethod.id) === String(method.id)
    ? latestSelectedMethod
    : method;
  const cryptoNetworkOptions = useMemo(() => cryptoNetworkOptionsFor(activeMethod || method), [activeMethod, method]);
  const selectedCryptoNetwork = cryptoNetworkOptions.find(option => option.code === cryptoNetwork) || cryptoNetworkOptions[0] || null;
  const accent = mode === 'deposit' ? '#22C55E' : '#F97316';
  const accentDark = mode === 'deposit' ? '#15803D' : '#C2410C';
  const balanceUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const withdrawalRules = walletRules?.withdrawal_rules || {};
  const maxWithdrawalPerDay = Number(
    withdrawalRules.max_total_per_day ??
    withdrawalRules.max_daily_withdraw_total ??
    0
  ) || 0;
  const enteredAmount = Number(amount);
  const parsedTipAmount = Number(tipAmount);
  const enteredTipAmount = mode === 'withdrawal' && Number.isFinite(parsedTipAmount) && parsedTipAmount > 0
    ? Math.round(parsedTipAmount * 100) / 100
    : 0;
  const tipsAllowed = cashoutSpeed !== 'express';
  const cashoutTipAmount = tipsAllowed && tipAgent ? enteredTipAmount : 0;
  const cashoutGrossAmount = mode === 'withdrawal' && Number.isFinite(enteredAmount) && enteredAmount > 0
    ? Math.round(enteredAmount * 100) / 100
    : 0;
  const cashoutBreakdown = calculateExpressCashoutBreakdown({
    grossAmount: cashoutGrossAmount,
    tipAmount: cashoutTipAmount,
    feePercent: withdrawalRules.express_fee_percent,
    express: cashoutSpeed === 'express',
  });
  const cashoutNetAmount = mode === 'withdrawal' ? cashoutBreakdown.amountAfterTip : cashoutGrossAmount;
  const expressFeePercent = cashoutBreakdown.feePercent;
  const expressFeeAmount = cashoutBreakdown.feeAmount;
  const cashoutPayoutAmount = cashoutBreakdown.payoutAmount;
  const minimumWithdrawalAmount = effectiveMinimumWithdrawal(withdrawalRules.min_amount);
  const belowMinimumWithdrawal = mode === 'withdrawal'
    && cashoutGrossAmount > 0
    && cashoutGrossAmount < minimumWithdrawalAmount;
  const hasTip = cashoutTipAmount > 0 && !!tipAgent;
  const expressDestinationError = cashoutSpeed === 'express' && method?.express_way_code
    ? expressPayoutValidationError(method.express_way_code, walletAddress, expressSecondary)
    : '';
  const withdrawalDetailsComplete = Boolean(
    String(payoutName || '').trim()
    && String(walletAddress || '').trim()
    && (cashoutSpeed !== 'express' || !expressDestinationError)
  );
  const exceedsDailyWithdrawalCap = mode === 'withdrawal'
    && maxWithdrawalPerDay > 0
    && cashoutGrossAmount > maxWithdrawalPerDay;
  const exceedsWalletBalance = mode === 'withdrawal'
    && cashoutGrossAmount > 0
    && cashoutGrossAmount > balanceUsd;
  const withdrawalAmountStepLocked = mode === 'withdrawal'
    && (
      !cashoutGrossAmount
      || belowMinimumWithdrawal
      || exceedsDailyWithdrawalCap
      || exceedsWalletBalance
      || cashoutTipAmount >= cashoutGrossAmount
      || cashoutNetAmount <= 0
      || cashoutPayoutAmount <= 0
    );
  const tipAgents = useMemo(() => (
    (Array.isArray(branding?.chat_agents) ? branding.chat_agents : [])
      .filter(agent => agent && agent.enabled !== false && (agent.name || agent.id))
  ), [branding?.chat_agents]);
  const resolveMediaUrl = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) return value;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${value}`;
  };
  const openManualQrPreview = (url, title) => {
    const resolved = resolveMediaUrl(url);
    if (!resolved) return;
    setManualQrPreview({ url: resolved, title: title || 'Payment QR' });
  };
  const brandLogoUrl = (() => {
    const raw = String(branding?.logo_url || branding?.intro_logo_url || '').trim();
    if (!raw) return '';
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${raw}`;
  })();

  useEffect(() => {
    setCashoutDetailsWarningOpen(mode === 'withdrawal' && step === 3);
  }, [mode, step]);

  const requestClose = () => {
    if (mode === 'deposit' && step === 2) {
      setStep(1);
      return;
    }
    if (step >= 2) {
      setConfirmCloseOpen(true);
    } else {
      close();
    }
  };

  const openSupportForLoad = () => {
    if (typeof onOpenSupport === 'function') {
      onOpenSupport();
      return;
    }
    toast.message('Open support from the chat button and our agents will help you load.');
  };

  const clearProofFile = () => {
    proofSelectionRef.current += 1;
    setProofFile(null);
    proofOptimizationPromiseRef.current = Promise.resolve(null);
    if (proofInputRef.current) proofInputRef.current.value = '';
  };

  const selectProofFile = async (event) => {
    const sourceFile = event.target.files?.[0] || null;
    const selectionId = proofSelectionRef.current + 1;
    proofSelectionRef.current = selectionId;
    setProofFile(sourceFile);
    if (!sourceFile) {
      proofOptimizationPromiseRef.current = Promise.resolve(null);
      return;
    }

    const optimizationPromise = optimizeProofImage(sourceFile);
    proofOptimizationPromiseRef.current = optimizationPromise;
    const optimized = await optimizationPromise;
    if (proofSelectionRef.current !== selectionId) return;
    setProofFile(optimized || sourceFile);
  };

  const copyToClipboard = async (value, label = 'Copied') => {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Could not copy');
    }
  };

  useEffect(() => {
    if (mode !== 'withdrawal' || cashoutSpeed === 'express') {
      setTipAgent(null);
      setTipAmount('');
    }
  }, [mode, cashoutSpeed]);

  useEffect(() => {
    if (!tipAgent) return;
    const selectedKey = String(tipAgent.id || tipAgent.name || '');
    const latest = tipAgents.find(agent => String(agent.id || agent.name || '') === selectedKey);
    if (!latest) {
      setTipAgent(null);
      setTipAmount('');
      return;
    }
    if (
      String(latest.name || '') !== String(tipAgent.name || '') ||
      String(latest.title || '') !== String(tipAgent.title || '') ||
      String(latest.avatar_url || '') !== String(tipAgent.avatar_url || '')
    ) {
      setTipAgent(latest);
    }
  }, [tipAgents, tipAgent]);

  useEffect(() => {
    if (cashoutSpeed === 'express') {
      const wayCode = String(method?.express_way_code || '');
      const selectedExpressMethod = EXPRESS_PAYOUT_METHODS.find(item => item.express_way_code === wayCode);
      if (method?.id && (!EXPRESS_WAY_CODES.has(wayCode) || selectedExpressMethod?.player_enabled !== true)) {
        setMethod(null);
      }
      return;
    }
    const latestMethod = method?.id
      ? filteredMethods.find(item => item.id === method.id)
      : null;
    if (method?.id && (!latestMethod || latestMethod.player_enabled === false)) {
      setMethod(null);
    }
  }, [cashoutSpeed, filteredMethods, method?.express_way_code, method?.id, setMethod]);

  useEffect(() => {
    setWalletAddress('');
    setExpressSecondary('');
    setPayoutName('');
    setPayoutDetails('');
  }, [method?.id, mode]);

  useEffect(() => {
    const currentMethod = activeMethod || method;
    if (mode !== 'deposit' || !isCryptoGatewayMethod(currentMethod)) {
      setCryptoNetwork('');
      return;
    }
    if (!cryptoNetworkOptions.some(option => option.code === cryptoNetwork)) {
      setCryptoNetwork(cryptoNetworkOptions[0]?.code || '');
    }
  }, [activeMethod, method, mode, cryptoNetwork, cryptoNetworkOptions]);

  useEffect(() => {
    if (mode === 'deposit') {
      setLatestSelectedMethod(null);
      return undefined;
    }
    if (!method?.id) {
      setLatestSelectedMethod(null);
      return undefined;
    }
    if (cashoutSpeed === 'express' && method?.express_way_code) {
      setLatestSelectedMethod(null);
      return undefined;
    }
    let cancelled = false;
    setLatestSelectedMethod(null);
    const flow = normalizedMode === 'withdraw' ? 'withdraw' : 'deposit';
    api.get(`/api/payment-methods/portal/${flow}`)
      .then(res => {
        if (cancelled) return;
        const list = res.data?.payment_methods || res.data?.methods || [];
        const fresh = list.find(item => String(item?.id) === String(method.id));
        if (fresh) {
          setLatestSelectedMethod({ ...method, ...fresh });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cashoutSpeed, method, method?.express_way_code, method?.id, mode, normalizedMode, step]);

  const stepperLabels = mode === 'withdrawal'
    ? (cashoutSpeed === 'express'
      ? ['Amount', 'Payout Method', 'Payout Details', 'Confirmation']
      : ['Amount & Tip', 'Cashout Method', 'Transaction Details', 'Confirmation'])
    : ['Payment method', 'Pay'];

  const submit = async () => {
    if (submitLockRef.current || processing) {
      return;
    }
    const selectedAmount = mode === 'withdrawal' ? cashoutNetAmount : Number(amount);
    if (mode === 'withdrawal') {
      if (!cashoutGrossAmount || cashoutGrossAmount <= 0) { toast.error('Enter cashout amount'); return; }
      if (exceedsWalletBalance) { toast.error(`Current balance is ${moneyLabel(balanceUsd)}. Enter ${moneyLabel(balanceUsd)} or less.`); return; }
      if (exceedsDailyWithdrawalCap) { toast.error(`Maximum withdrawal per day is ${moneyLabel(maxWithdrawalPerDay)}`); return; }
      if (cashoutTipAmount >= cashoutGrossAmount) { toast.error('Tip must be lower than the cashout amount'); return; }
      if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) { toast.error('Withdrawal amount must be greater than $0'); return; }
    } else if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) {
      toast.error('Select a load amount');
      return;
    }
    if (!method) { toast.error('Pick a payment method'); return; }
    submitLockRef.current = true;
    setProcessing(true);
    try {
      if (mode === 'deposit') {
        // Validate the returned row, not a React state update's stale closure.
        // An admin may have changed the minimum since this sheet was opened.
        const { method: selectedMethod, sessionToken } = await depositRules.refresh();
        const methodMinAmount = minAmountForDepositMethod(selectedMethod);
        if (methodMinAmount > 0 && selectedAmount < methodMinAmount) {
          toast.error(depositMinimumMessage(selectedMethod, methodMinAmount));
          setProcessing(false);
          return;
        }
        const isGateway = isApiGatewayMethod(selectedMethod);
        if (!isGateway && !proofFile) {
          toast.error('Upload payment screenshot before submitting');
          setProcessing(false);
          return;
        }
        if (isGateway) {
          const shouldTreatAsApplePay = isApplePayGateway(selectedMethod);
          const useExternalPaymentLink = shouldUseExternalPaymentLink(selectedMethod);
          const gatewayAmount = Math.round(Number(selectedAmount || 0) * 100) / 100;
          const cryptoGateway = isCryptoGatewayMethod(selectedMethod);
          const minAmount = minAmountForDepositMethod(selectedMethod);
          const maxAmount = Number(selectedMethod?.max_amount || 0);
          if (!gatewayAmount) {
            toast.error('Select a load amount');
            setProcessing(false);
            return;
          }
          if (minAmount > 0 && gatewayAmount < minAmount) {
            toast.error(depositMinimumMessage(selectedMethod, minAmount));
            setProcessing(false);
            return;
          }
          if (maxAmount > 0 && gatewayAmount > maxAmount) {
            toast.error(`Maximum amount for ${selectedMethod.title || 'this method'} is $${maxAmount}`);
            setProcessing(false);
            return;
          }
          if (cryptoGateway && !selectedCryptoNetwork?.code) {
            toast.error('Select a crypto network');
            setProcessing(false);
            return;
          }
          const idem = `gw-deposit-${user?.id || 'anon'}-${Date.now()}`;
          const gatewayMethod = canonicalApiGatewayCode(selectedMethod);
          const gatewaySubmethod = canonicalApiGatewayCode(selectedMethod) || gatewaySubmethodCode(selectedMethod);
          const selectedGatewaySubmethod = cryptoGateway ? selectedCryptoNetwork.code : gatewaySubmethod;
          setCheckoutPanel({
            loading: true,
            url: '',
            originalUrl: '',
            orderId: null,
            amount: gatewayAmount,
            methodTitle: selectedMethod.title,
            cryptoNetwork: cryptoGateway ? selectedCryptoNetwork.label : '',
            cryptoPayment: null,
            applePay: shouldTreatAsApplePay,
            linkReady: useExternalPaymentLink,
          });
          const res = await api.post(
            '/api/gateway/deposit',
            {
              amount: gatewayAmount,
              method: gatewayMethod,
              currency: 'USD',
              gateway_submethod: selectedGatewaySubmethod,
              ...(cryptoGateway ? { crypto_network: selectedCryptoNetwork.code } : {}),
            },
            { headers: { 'X-Idempotency-Key': idem }, expectedSessionToken: sessionToken }
          );
          const payUrl = (
            res.data?.pay_url
            || res.data?.payment_url
            || res.data?.payment_link
            || res.data?.checkout_url
            || res.data?.checkoutUrl
            || res.data?.url
            || res.data?.link
          );
          const cryptoPayment = res.data?.crypto_payment || null;
          const hasCryptoPaymentDetails = Boolean(cryptoPayment?.pay_address || cryptoPayment?.payment_url);
          if (!payUrl && !hasCryptoPaymentDetails) {
            const message = 'Gateway did not return a payment URL';
            toast.error(message);
            setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
            setProcessing(false);
            return;
          }
          toast.success(cryptoPayment?.pay_address ? 'Crypto payment details ready' : (useExternalPaymentLink ? 'Payment link ready' : 'Opening secure checkout...'));
          setCheckoutPanel(prev => ({
            ...(prev || {}),
            loading: false,
            linkReady: useExternalPaymentLink && !cryptoPayment?.pay_address,
            url: (useExternalPaymentLink || cryptoPayment?.pay_address) ? payUrl : iframeCheckoutUrl(payUrl, shouldTreatAsApplePay),
            originalUrl: cryptoPayment?.payment_url || payUrl,
            orderId: res.data?.order_id,
            amount: res.data?.charged_amount || gatewayAmount,
            methodTitle: selectedMethod.title,
            cryptoNetwork: cryptoGateway
              ? cryptoNetworkLabel(cryptoPayment?.pay_currency || selectedCryptoNetwork.code, selectedCryptoNetwork.label)
              : '',
            cryptoPayment,
            applePay: shouldTreatAsApplePay,
          }));
          return;
        }
        const jobKey = String(user?.id || 'anonymous-player');
        if (activeManualDepositJobs.has(jobKey)) {
          toast.message('Your previous deposit is already being submitted.');
          close();
          return;
        }

        const manualDepositIdem = `manual-deposit-${jobKey}-${Date.now()}`;
        const selectedProofPromise = proofOptimizationPromiseRef.current;
        const selectedMethodId = selectedMethod.id;
        const job = (async () => {
          const selectedProof = await selectedProofPromise;
          const pending = pendingManualDepositRef.current;
          const canReusePending = pending
            && Number(pending.amount) === Number(selectedAmount)
            && String(pending.paymentMethodId) === String(selectedMethodId);
          let txnId = canReusePending ? pending.txnId : null;
          if (!txnId) {
            const res = await runWithBoundedManualRetry(() => api.post(
              '/api/requests/deposit',
              { amount: selectedAmount, payment_method_id: selectedMethodId },
              { headers: { 'X-Idempotency-Key': manualDepositIdem }, expectedSessionToken: sessionToken }
            ));
            txnId = res.data.id;
            pendingManualDepositRef.current = {
              txnId,
              amount: selectedAmount,
              paymentMethodId: selectedMethodId,
            };
          }
          if (!selectedProof || !txnId) throw new Error('Deposit proof could not be prepared');

          await runWithBoundedManualRetry(() => {
            const fd = new FormData();
            fd.append('file', selectedProof);
            return api.post(`/api/requests/deposit/${txnId}/upload-proof`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' }, expectedSessionToken: sessionToken,
            });
          });
          pendingManualDepositRef.current = null;
        })();

        activeManualDepositJobs.set(jobKey, job);
        try {
          await job;
          toast.success('Deposit screenshot submitted.');
          refresh?.();
          close();
        } catch (error) {
          toast.error(proofUploadErrorMessage(error));
          refresh?.();
        } finally {
          activeManualDepositJobs.delete(jobKey);
        }
        return;
      } else {
        const labels = cashoutMethodLabels(activeMethod || method);
        const expressWayCode = cashoutSpeed === 'express' ? String(method?.express_way_code || '') : '';
        const cleanPayoutName = String(payoutName || '').trim();
        const cleanPayoutTag = String(walletAddress || '').trim();
        if (!cleanPayoutName || !cleanPayoutTag) {
          toast.error(cashoutSpeed === 'express'
            ? `Enter recipient name and ${method?.primaryLabel || 'payout details'}`
            : `Enter ${labels.nameLabel} and ${labels.tagLabel}`);
          setProcessing(false);
          return;
        }
        if (cashoutSpeed === 'express') {
          const destinationError = expressPayoutValidationError(expressWayCode, cleanPayoutTag, expressSecondary);
          if (destinationError) {
            toast.error(destinationError);
            setProcessing(false);
            return;
          }
        }
        const normalizedPayoutDetails = {
          method_label: cashoutSpeed === 'express' ? method.title : labels.methodName,
          label_name: cashoutSpeed === 'express' ? 'Recipient name' : labels.nameLabel,
          label_tag: cashoutSpeed === 'express' ? method.primaryLabel : labels.tagLabel,
          account_name: cleanPayoutName,
          payout_name: cleanPayoutName,
          name: cleanPayoutName,
          account_tag: cleanPayoutTag,
          payout_tag: cleanPayoutTag,
          tag: cleanPayoutTag,
          address: cleanPayoutTag,
          wallet_address: cleanPayoutTag,
          cashout_speed: cashoutSpeed === 'express' ? 'express' : 'normal',
          cashout_speed_label: cashoutSpeed === 'express' ? 'Express Cashout' : 'Normal Cashout',
          express_fee_percent_preview: expressFeePercent,
          express_fee_amount_preview: expressFeeAmount,
          ...(cashoutSpeed === 'express'
            ? buildExpressPayoutDetails(expressWayCode, cleanPayoutTag, expressSecondary)
            : {}),
        };
        const cleanNote = String(payoutDetails || '').trim();
        if (cleanNote) {
          normalizedPayoutDetails.details = cleanNote;
          normalizedPayoutDetails.note = cleanNote;
        }
        await api.post('/api/requests/withdraw', {
          amount: selectedAmount,
          payment_method_id: cashoutSpeed === 'express' ? null : method.id,
          payment_method_name: cashoutSpeed === 'express' ? method.title : undefined,
          payout_details: normalizedPayoutDetails,
          tip_amount: hasTip ? cashoutTipAmount : 0,
          tip_agent_id: hasTip ? (tipAgent?.id || '') : '',
          tip_agent_name: hasTip ? (tipAgent?.name || '') : '',
          tip_agent_title: hasTip ? (tipAgent?.title || '') : '',
          tip_agent_avatar_url: hasTip ? (tipAgent?.avatar_url || '') : '',
        });
        toast.success('Cashout request submitted!');
        await checkAuth();
      }
      refresh();
      close();
    } catch (err) {
      const message = err.paymentRulesError ? err.message : isApiGatewayMethod(activeMethod || method)
        ? 'Payment link could not be created. Please try again.'
        : (err.response?.data?.detail || 'Request failed');
      toast.error(message);
      if (isApiGatewayMethod(activeMethod || method)) {
        setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
      }
    } finally {
      submitLockRef.current = false;
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (!checkoutPanel?.orderId) return undefined;
    let stopped = false;
    let tries = 0;

    const poll = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const res = await api.post(`/api/deposits/${checkoutPanel.orderId}/poll-status`);
        const status = String(res.data?.status || res.data?.gateway_status || '').toUpperCase();
        if (['APPROVED', 'SUCCESS', 'PAID', 'COMPLETED', 'SETTLED'].includes(status)) {
          toast.success('Payment verified. Wallet updated.');
          await checkAuth?.();
          refresh?.();
          setCheckoutPanel(prev => (prev ? { ...prev, verified: true } : prev));
          return;
        }
        if (['FAILED', 'EXPIRED'].includes(status)) {
          const message = status === 'EXPIRED'
            ? 'Payment link expired. No wallet credit was added.'
            : 'Payment was not completed.';
          toast.error(message);
          setCheckoutPanel(prev => (prev ? { ...prev, failed: true, errorMessage: message } : prev));
          return;
        }
      } catch {
        // Keep polling; website providers can take a few seconds to settle.
      }
      if (!stopped && tries < 90) {
        window.setTimeout(poll, tries < 5 ? 2500 : 5000);
      }
    };

    const timer = window.setTimeout(poll, 2500);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [checkoutPanel, checkAuth, close, refresh]);

  const moneyLabel = (value) => `$${Number(value || 0).toFixed(2)}`;
  const validateWithdrawalAmountStep = () => {
    if (!cashoutGrossAmount || cashoutGrossAmount <= 0) {
      toast.error('Confirm your cashout amount');
      return false;
    }
    if (belowMinimumWithdrawal) {
      toast.error(`Minimum withdrawal amount is ${moneyLabel(minimumWithdrawalAmount)}`);
      return false;
    }
    if (exceedsWalletBalance) {
      toast.error(`Current balance is ${moneyLabel(balanceUsd)}. Enter ${moneyLabel(balanceUsd)} or less.`);
      return false;
    }
    if (exceedsDailyWithdrawalCap) {
      toast.error(`Maximum withdrawal per day is ${moneyLabel(maxWithdrawalPerDay)}`);
      return false;
    }
    if (cashoutTipAmount >= cashoutGrossAmount) {
      toast.error('Tip must be lower than the cashout amount');
      return false;
    }
    if (cashoutNetAmount <= 0) {
      toast.error('Withdrawal amount must be greater than $0');
      return false;
    }
    return true;
  };

  const depositAmountBelowMinimum = (selectedMethod = activeMethod || method) => {
    const minAmount = minAmountForDepositMethod(selectedMethod);
    const selectedAmount = Number(amount);
    return minAmount > 0 && (!Number.isFinite(selectedAmount) || selectedAmount < minAmount);
  };

  const renderMethodList = (intro = 'Pick a payment method to continue.') => (
    <div className="space-y-3" data-testid="wallet-step-method">
      <p className="text-sm" style={{ color: '#6B7280' }}>{intro}</p>
      {filteredMethods.length === 0 ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: '#F3F4F6' }}>
          <p className="font-bold" style={{ color: '#0B0F0C' }}>No methods available</p>
          <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Please contact support.</p>
        </div>
      ) : filteredMethods.map(m => {
        const selected = method?.id === m.id;
        const playerEnabled = m.player_enabled !== false;
        const badgeLabel = paymentMethodBadgeLabel(m, mode);
        const badgeStyle = paymentBadgeStyle(m.player_badge_tone, accent);
        return (
          <div key={m.id}>
            <button
              type="button"
              onClick={() => playerEnabled && setMethod(m)}
              disabled={!playerEnabled}
              className="w-full p-4 rounded-2xl flex items-center gap-4 transition active:scale-[0.99] relative overflow-hidden disabled:cursor-not-allowed disabled:active:scale-100"
              style={{
                background: selected ? `${accent}10` : '#F8FAFC',
                border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
                boxShadow: selected ? `0 6px 18px ${accent}33` : 'none',
                opacity: playerEnabled ? 1 : 0.48,
                filter: playerEnabled ? 'none' : 'grayscale(0.35)',
              }}
              aria-disabled={!playerEnabled}
              data-testid={`fs-pay-method-${m.id}`}
            >
              <PaymentMethodLogo method={m} accent={accent} accentDark={accentDark} />
              <div className="min-w-0 flex-1 text-left">
                <p className="display break-words text-base font-bold" style={{ color: '#0B0F0C' }}>{m.title}</p>
                <div className="flex gap-2 mt-0.5 flex-wrap">
                  {m.bonus_percent > 0 && mode === 'deposit' && (
                    <span className="text-[11px] font-bold" style={{ color: '#15803D' }}>+{m.bonus_percent}% bonus</span>
                  )}
                  {m.min_amount && (<span className="text-[11px] font-extrabold" style={{ color: '#334155' }}>Min ${m.min_amount}</span>)}
                </div>
              </div>
              <div className="ml-auto flex max-w-[92px] shrink-0 flex-col items-end gap-2 min-[380px]:max-w-[118px]">
                {badgeLabel && (
                  <span
                    className="payment-method-hot-badge max-w-full"
                    style={{ ...badgeStyle, maxWidth: '100%' }}
                  >
                    <Sparkles className="h-3 w-3" />
                    {badgeLabel}
                  </span>
                )}
                {selected ? (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
                    <Check className="w-4 h-4 text-white" strokeWidth={4} />
                  </div>
                ) : (
                  <ChevronDown className="w-4 h-4 -rotate-90" style={{ color: '#9CA3AF' }} />
                )}
              </div>
            </button>
            {!playerEnabled && (
              <p className="mt-1.5 px-2 text-left text-xs font-semibold" style={{ color: '#B45309' }}>
                {m.disabled_message || 'Please contact support to enable it'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderWithdrawalAmountStep = () => {
    const quickAmounts = [20, 25, 50, 100, 250];
    const tipOptions = [5, 10, 25, 50];
    return (
      <div className="space-y-5" data-testid="wallet-step-withdrawal-amount">
        <div className="rounded-2xl p-5" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Amount to withdraw</p>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
            {tipsAllowed
              ? 'Confirm the wallet amount first, then add an optional agent tip below.'
              : 'Confirm the amount you want to send through Express Cashout.'}
          </p>
          <p className="mt-2 text-xs font-extrabold" style={{ color: accentDark }}>
            Minimum withdrawal: {moneyLabel(minimumWithdrawalAmount)}
          </p>
          {maxWithdrawalPerDay > 0 && (
            <div
              className="mt-3 rounded-xl px-3 py-2 text-xs font-extrabold"
              style={{
                background: exceedsDailyWithdrawalCap ? '#FEF2F2' : '#FFFBEB',
                border: `1px solid ${exceedsDailyWithdrawalCap ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}`,
                color: exceedsDailyWithdrawalCap ? '#991B1B' : '#92400E',
              }}
              data-testid="withdrawal-daily-cap"
            >
              Max withdrawal per day: {moneyLabel(maxWithdrawalPerDay)}
            </div>
          )}
          <Input
            type="number"
            min={minimumWithdrawalAmount}
            max={Math.max(balanceUsd, 0)}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0.00"
            className="h-14 text-2xl font-bold mono mt-4"
            style={{
              background: '#FFFFFF',
              border: `2px solid ${exceedsWalletBalance ? 'rgba(239,68,68,0.55)' : `${accent}55`}`,
              color: '#0B0F0C',
            }}
            data-testid="fs-wallet-amount"
            autoFocus
          />
          {exceedsWalletBalance && (
            <div
              className="mt-3 rounded-xl px-3 py-2 text-xs font-extrabold"
              style={{
                background: '#FEF2F2',
                border: '1px solid rgba(239,68,68,0.35)',
                color: '#991B1B',
              }}
              data-testid="withdrawal-balance-lock"
            >
              Current balance is {moneyLabel(balanceUsd)}. Enter {moneyLabel(balanceUsd)} or less to continue.
            </div>
          )}
          {belowMinimumWithdrawal && (
            <div
              className="mt-3 rounded-xl px-3 py-2 text-xs font-extrabold"
              style={{
                background: '#FEF2F2',
                border: '1px solid rgba(239,68,68,0.35)',
                color: '#991B1B',
              }}
              data-testid="withdrawal-minimum-lock"
            >
              Enter at least {moneyLabel(minimumWithdrawalAmount)} to continue.
            </div>
          )}
          <div className="grid grid-cols-5 gap-2 mt-3">
            {quickAmounts.map(v => {
              const selected = Number(amount) === v;
              const locked = v > balanceUsd;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAmount(String(v))}
                  disabled={locked}
                  className="py-2 rounded-xl text-sm font-bold display transition disabled:cursor-not-allowed"
                  style={{
                    background: locked ? '#E5E7EB' : (selected ? accent : '#F3F4F6'),
                    color: locked ? '#9CA3AF' : (selected ? '#FFFFFF' : '#0B0F0C'),
                    border: locked ? '1px solid rgba(156,163,175,0.35)' : '1px solid transparent',
                  }}
                >
                  ${v}
                </button>
              );
            })}
          </div>
        </div>

        {tipsAllowed && (
        <div className="rounded-2xl p-5 space-y-4" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.08)' }} data-testid="cashout-tip-section">
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Optional tip to your favorite agent</p>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Optional. Pick an agent first, then choose a tip.</p>
          </div>

          {tipAgents.length === 0 ? (
            <div className="rounded-xl p-4 text-sm" style={{ background: '#FFFFFF', color: '#6B7280', border: '1px dashed rgba(0,0,0,0.14)' }}>
              No agents available for tips right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tipAgents.map(agent => {
                const selected = String(tipAgent?.id || tipAgent?.name) === String(agent.id || agent.name);
                const avatar = resolveMediaUrl(agent.avatar_url);
                return (
                  <button
                    key={agent.id || agent.name}
                    type="button"
                    onClick={() => {
                      setTipAgent(agent);
                      if (!tipAmount) setTipAmount('5');
                    }}
                    className="p-3 rounded-2xl flex items-center gap-3 text-left transition active:scale-[0.99]"
                    style={{
                      background: selected ? `${accent}12` : '#FFFFFF',
                      border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
                      boxShadow: selected ? `0 8px 18px ${accent}25` : 'none',
                    }}
                  >
                    <span className="w-12 h-12 rounded-full overflow-hidden shrink-0 flex items-center justify-center" style={{ background: `${accent}18`, color: accentDark }}>
                      {avatar ? (
                        <SafeImage
                          src={avatar}
                          alt={agent.name || 'Agent'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          fallback={<UserRound className="w-6 h-6" />}
                        />
                      ) : (
                        <UserRound className="w-6 h-6" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="display font-extrabold block truncate" style={{ color: '#0B0F0C' }}>{agent.name || 'Agent'}</span>
                      <span className="text-xs block truncate" style={{ color: '#6B7280' }}>{agent.title || 'Support Agent'}</span>
                    </span>
                    {selected && <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: accentDark }} />}
                  </button>
                );
              })}
            </div>
          )}

          {tipAgent && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {tipOptions.map(v => {
                  const selected = Number(tipAmount) === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTipAmount(String(v))}
                      className="py-2 rounded-xl text-sm font-bold display transition"
                      style={{
                        background: selected ? accent : '#EEF2F7',
                        color: selected ? '#FFFFFF' : '#0B0F0C',
                      }}
                    >
                      ${v}
                    </button>
                  );
                })}
              </div>
              <Input
                type="number"
                min="0"
                value={tipAmount}
                onChange={e => setTipAmount(e.target.value)}
                placeholder="Custom tip amount"
                className="h-12"
                style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
                data-testid="wallet-tip-amount"
              />
              <button
                type="button"
                onClick={() => { setTipAgent(null); setTipAmount(''); }}
                className="text-xs display font-bold"
                style={{ color: '#6B7280' }}
              >
                Remove tip
              </button>
            </div>
          )}
        </div>
        )}

        <div className="rounded-2xl p-5 space-y-2 text-sm" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <Row label="Wallet deduction" value={moneyLabel(cashoutGrossAmount)} valueBold />
          {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
          <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
          {maxWithdrawalPerDay > 0 && <Row label="Daily max" value={moneyLabel(maxWithdrawalPerDay)} valueBold={exceedsDailyWithdrawalCap} />}
        </div>
      </div>
    );
  };

  const renderDepositAmountStep = () => {
    const displayMethod = activeMethod || method;
    const supportContactMethod = isSupportContactMethod(displayMethod);
    const isECashapp = isECashappGatewayMethod(displayMethod);
    const isCashappQr = isCashappQrMethod(displayMethod);
    const isGateway = isApiGatewayMethod(displayMethod) || isECashapp;
    const cryptoGateway = isCryptoGatewayMethod(displayMethod);
    const quickAmounts = isECashapp ? [] : amountOptionsFor(displayMethod);
    const amountNumber = Number(amount);
    const effectiveMinAmount = minAmountForDepositMethod(displayMethod);
    const cashappQrAmountLocked = isCashappQr && (
      !Number.isFinite(amountNumber) || amountNumber < effectiveMinAmount
    );
    const hasExplicitAmountOptions = hasExplicitGatewayAmountOptions(displayMethod);
    const gatewayAllowsCustomAmount = isGateway && (
      displayMethod?.allow_custom_amount === true
      || (!hasExplicitAmountOptions && quickAmounts.length === 0)
      || canonicalApiGatewayCode(displayMethod) === 'venmo'
      || isECashapp
      || canonicalApiGatewayCode(displayMethod) === 'crypto'
    );
    const showCustomInput = !isGateway || gatewayAllowsCustomAmount;
    const useChimeAutoAmountGrid = isChimeAutoPaymentMethod(displayMethod)
      && quickAmounts.length > 0
      && !gatewayAllowsCustomAmount;
    const useDropdownAmountPicker = isGateway
      && displayMethod?.amount_selection_mode === 'dropdown'
      && quickAmounts.length > 0
      && !gatewayAllowsCustomAmount
      && !useChimeAutoAmountGrid;
    const showQuickAmounts = useChimeAutoAmountGrid
      || (!useDropdownAmountPicker && !isECashapp && !isCashappQr && !gatewayAllowsCustomAmount && (quickAmounts.length > 0 || !isGateway));

    if (depositRulesLocked && !depositRules.method) {
      return (
        <div className="space-y-4" data-testid="deposit-rules-state">
          <p role={depositRules.error ? 'alert' : 'status'} className="text-sm font-semibold" style={{ color: '#334155' }}>
            {depositRules.error || 'Checking the latest payment rules…'}
          </p>
          {depositRules.error && (
            <button
              type="button"
              onClick={() => { depositRules.refresh().catch(() => {}); }}
              className="min-h-11 rounded-xl border border-slate-300 px-4 text-sm font-bold text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              data-testid="deposit-rules-retry"
            >
              Retry payment rules
            </button>
          )}
          <button type="button" onClick={() => setStep(1)} className="block min-h-11 text-sm font-bold underline underline-offset-4" style={{ color: accentDark }}>
            Choose another payment method
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-5" data-testid="wallet-step-amount">
        {depositRules.status === 'loading' && (
          <p role="status" className="text-sm font-semibold" style={{ color: '#334155' }}>Checking the latest payment rules…</p>
        )}
        <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.08)' }}>
          <PaymentMethodLogo method={displayMethod} accent={accent} accentDark={accentDark} size="sm" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Method</p>
            <p className="display font-bold" style={{ color: '#0B0F0C' }}>{displayMethod.title}</p>
          </div>
          <button onClick={() => setStep(1)} className="text-xs font-bold display" style={{ color: accentDark }}>Change</button>
        </div>

        {supportContactMethod ? (
          <div className="rounded-2xl p-5 space-y-4" style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.35)' }}>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-extrabold" style={{ color: '#15803D' }}>
                Contact support for load
              </p>
              <p className="display text-xl font-extrabold mt-2" style={{ color: '#0B0F0C' }}>
                Our agent will help you complete this load.
              </p>
            </div>
            <ManualPaymentDetails method={displayMethod} accent={accent} accentDark={accentDark} showQr={false} />
            <button
              type="button"
              onClick={openSupportForLoad}
              className="w-full h-12 rounded-xl display font-extrabold"
              style={{ background: 'linear-gradient(180deg,#22C55E,#15803D)', color: '#FFFFFF', boxShadow: '0 8px 22px rgba(34,197,94,0.35)' }}
              data-testid="wallet-open-support-load"
            >
              Open Support →
            </button>
          </div>
        ) : (
            <div>
              {cryptoGateway && (
                <div className="mb-5">
                  <label className="text-xs uppercase tracking-wider mb-2 block font-bold" style={{ color: '#6B7280' }}>
                    Crypto Network
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {cryptoNetworkOptions.map(option => {
                      const selected = selectedCryptoNetwork?.code === option.code;
                      return (
                        <button
                          key={option.code}
                          type="button"
                          onClick={() => setCryptoNetwork(option.code)}
                          disabled={processing}
                          className="h-12 rounded-xl display text-sm font-extrabold transition disabled:opacity-60"
                          style={{
                            background: selected ? '#0F172A' : '#F3F4F6',
                            color: selected ? '#FFFFFF' : '#0B0F0C',
                            border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
                            boxShadow: selected ? `0 8px 18px ${accent}33` : 'none',
                          }}
                          data-testid={`crypto-network-${option.code}`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <label className="text-xs uppercase tracking-wider mb-2 block font-bold" style={{ color: '#6B7280' }}>
                {isGateway && quickAmounts.length && !gatewayAllowsCustomAmount ? 'Select Load Amount' : 'Amount ($)'}
              </label>
              {isCashappQr && (
                <div
                  className="mb-3 rounded-2xl p-3 text-xs font-extrabold leading-snug"
                  style={{ background: '#FFFBEB', border: '1px solid rgba(245,158,11,0.45)', color: '#92400E' }}
                  data-testid="cashapp-qr-minimum-note"
                >
                  Cashapp QR payments must be $20 or more. Payments below $20 will not be added to your wallet. For smaller loads, use CashApp via Speed or Cash App via link.
                </div>
              )}
              {isECashapp && (
                <div
                  className="mb-3 rounded-2xl p-3 text-xs font-bold leading-snug"
                  style={{ background: '#ECFDF5', border: '1px solid rgba(34,197,94,0.35)', color: '#166534' }}
                  data-testid="ecashapp-lightning-note"
                >
                  Note: your Cash App must be verified to pay via Lightning.
                </div>
              )}
              {showCustomInput && (
                <Input
                  type="number"
                  min={effectiveMinAmount}
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder={effectiveMinAmount > 0 ? `Minimum $${effectiveMinAmount}` : '0.00'}
                  className="h-14 text-2xl font-bold mono"
                  style={{ background: '#fff', border: `2px solid ${accent}55`, color: '#0B0F0C' }}
                  data-testid="fs-wallet-amount"
                  autoFocus
                />
              )}
              {useDropdownAmountPicker && (
                <div className="space-y-2" data-testid="chime-auto-amount-picker">
                  <select
                    value={amount}
                    onChange={event => setAmount(event.target.value)}
                    disabled={processing}
                    className="h-14 w-full rounded-xl px-4 text-base font-bold outline-none disabled:opacity-60"
                    style={{ background: '#FFFFFF', border: `2px solid ${accent}55`, color: '#0B0F0C' }}
                    data-testid="chime-auto-amount-select"
                  >
                    <option value="">Select a supported amount</option>
                    {quickAmounts.map(value => (
                      <option key={value} value={String(value)}>${Number(value).toFixed(2)}</option>
                    ))}
                  </select>
                  <p className="text-xs font-bold" style={{ color: '#3F6D52' }}>
                    Takes 3–4 minutes to load. Your wallet updates automatically after verification.
                  </p>
                </div>
              )}
              {showQuickAmounts && (
                <div
                  className={`grid grid-cols-3 gap-2 mt-3 ${useChimeAutoAmountGrid ? '' : 'sm:grid-cols-5'}`}
                  data-testid={useChimeAutoAmountGrid ? 'chime-auto-amount-grid' : undefined}
                >
                  {(quickAmounts.length ? quickAmounts : [5, 10, 25, 50, 100]).map(v => {
                    const display = Number.isInteger(Number(v)) ? `$${v}` : `$${Number(v).toFixed(2)}`;
                    const valStr = String(v);
                    const selected = Number(amount) === Number(v);
                    return (
                      <button
                        key={valStr}
                        onClick={() => setAmount(valStr)}
                        disabled={processing}
                        className={`${useChimeAutoAmountGrid ? 'h-10' : 'py-2'} rounded-xl text-sm font-bold display transition disabled:opacity-60`}
                        style={{
                          background: selected ? accent : '#F3F4F6',
                          color: selected ? '#fff' : '#0B0F0C',
                          boxShadow: selected ? `0 0 0 2px ${accent}33` : 'none',
                        }}
                        aria-pressed={selected}
                        data-testid={`quick-${valStr}`}
                      >
                        {processing && selected ? '...' : display}
                      </button>
                    );
                  })}
                </div>
              )}
              {cryptoGateway && (
                <p className="text-xs mt-3 font-bold" style={{ color: '#6B7280' }}>
                  Minimum crypto deposit is $20. Your link will use {selectedCryptoNetwork?.label || 'the selected network'} and the exact amount entered.
                </p>
              )}
              {isGateway && quickAmounts.length > 0 && !gatewayAllowsCustomAmount && (
                <p className="text-xs mt-3" style={{ color: '#6B7280' }}>
                  Select an amount, then continue to checkout.
                </p>
              )}
              {isGateway && quickAmounts.length > 0 && gatewayAllowsCustomAmount && !isECashapp && (
                <p className="text-xs mt-3" style={{ color: '#6B7280' }}>
                  Enter a custom amount or use a quick amount, then continue to checkout.
                </p>
              )}
              {isGateway && quickAmounts.length === 0 && !gatewayAllowsCustomAmount && (
                <div className="rounded-2xl p-4 text-center" style={{ background: '#F3F4F6' }}>
                  <p className="font-bold" style={{ color: '#0B0F0C' }}>No load amounts configured</p>
                  <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Please contact support.</p>
                </div>
              )}
            </div>
        )}

        {mode === 'deposit' && displayMethod && !supportContactMethod && (() => {
          const isGateway = isApiGatewayMethod(displayMethod) || isECashappGatewayMethod(displayMethod);
          if (isGateway) {
            return (
              <div
                className="rounded-2xl p-4 flex items-start gap-3"
                style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.4)' }}
                data-testid="gateway-instant-notice"
              >
                <span
                  className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center"
                  style={{ background: '#22C55E', boxShadow: '0 0 12px rgba(34,197,94,0.55)' }}
                >
                  <Sparkles className="w-5 h-5 text-white" />
                </span>
                <div className="text-sm leading-snug" style={{ color: '#0F2D1A' }}>
                  <p className="font-extrabold display">Instant secure checkout</p>
                  <p className="mt-1" style={{ color: '#3F6D52' }}>
                    Your secure {displayMethod.title} checkout opens from a payment link. Your wallet
                    is credited automatically the moment payment is verified — no screenshot needed.
                  </p>
                </div>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              <ManualPaymentDetails
                method={displayMethod}
                accent={accent}
                accentDark={accentDark}
                showQr
                onPreviewQr={openManualQrPreview}
                beforeQr={(
                  <>
                    <input type="file" ref={proofInputRef} accept="image/*" className="hidden" onChange={selectProofFile} />
                    <div
                      onClick={() => {
                        if (isCashappQr && cashappQrAmountLocked) {
                          toast.error(depositMinimumMessage(displayMethod, effectiveMinAmount));
                          return;
                        }
                        proofInputRef.current?.click();
                      }}
                      className={`border-2 border-dashed rounded-2xl p-4 text-center ${isCashappQr && cashappQrAmountLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      style={{
                        borderColor: isCashappQr && cashappQrAmountLocked ? 'rgba(245,158,11,0.45)' : 'rgba(34,197,94,0.4)',
                        background: isCashappQr && cashappQrAmountLocked ? '#FFFBEB' : '#F0FDF4',
                      }}
                    >
                      {proofFile ? (
                        <div className="flex items-center justify-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} />
                          <span className="min-w-0 text-left font-bold" style={{ color: '#0B0F0C' }}>
                            <span className="block truncate">Payment screenshot selected</span>
                          </span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); clearProofFile(); }}><XCircle className="w-4 h-4" style={{ color: '#9CA3AF' }} /></button>
                        </div>
                      ) : (
                        <p
                          className="text-sm font-bold"
                          style={{ color: isCashappQr && cashappQrAmountLocked ? '#92400E' : '#15803D' }}
                        >
                          {isCashappQr && cashappQrAmountLocked
                            ? 'Enter $20 or more before uploading a payment screenshot.'
                            : 'Click to upload payment screenshot (required)'}
                        </p>
                      )}
                    </div>
                  </>
                )}
              />
            </div>
          );
        })()}
      </div>
    );
  };

  const renderSelectedCashoutMethod = () => {
    if (!method) return null;
    return (
      <div
        className="w-full p-4 rounded-2xl flex items-center gap-4"
        style={{
          background: `${accent}08`,
          border: `2px solid ${accent}`,
          boxShadow: `0 8px 22px ${accent}22`,
        }}
        data-testid="wallet-selected-cashout-method"
      >
        <PaymentMethodLogo method={method} accent={accent} accentDark={accentDark} />
        <div className="flex-1 text-left">
          <p className="display text-base font-bold" style={{ color: '#0B0F0C' }}>{method.title}</p>
          {method.min_amount && (<p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Min ${method.min_amount}</p>)}
        </div>
        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
          <Check className="w-4 h-4 text-white" strokeWidth={4} />
        </div>
      </div>
    );
  };

  const renderWithdrawalSummary = () => (
    <div className="rounded-2xl p-4 text-sm space-y-2" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
      <Row label="Confirmed amount" value={moneyLabel(cashoutGrossAmount)} valueBold />
      {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
      {cashoutSpeed === 'express' && (
        <Row label={`Express fee (${expressFeePercent}%)`} value={moneyLabel(expressFeeAmount)} valueBold />
      )}
      <Row label="You receive" value={moneyLabel(cashoutPayoutAmount)} valueBold />
    </div>
  );

  const renderWithdrawalMethodStep = () => (
    <div className="space-y-5" data-testid="wallet-step-withdrawal-method">
      {renderWithdrawalSummary()}
      {cashoutSpeed === 'express' ? (
        <div className="space-y-3" data-testid="express-payout-methods">
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Where should we send your payout?</p>
            <p className="mt-1 text-xs font-medium" style={{ color: '#6B7280' }}>Choose a supported Express destination. Normal cashout methods are not used here.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {PLAYER_VISIBLE_EXPRESS_PAYOUT_METHODS.map(item => {
              const selected = method?.express_way_code === item.express_way_code;
              const playerEnabled = item.player_enabled === true;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (playerEnabled) setMethod(item);
                  }}
                  disabled={!playerEnabled}
                  aria-disabled={!playerEnabled}
                  className={`flex items-center gap-3 rounded-2xl p-4 text-left transition ${playerEnabled ? 'active:scale-[0.99]' : 'cursor-not-allowed'}`}
                  style={{
                    background: selected ? '#F5F3FF' : playerEnabled ? '#F8FAFC' : '#F3F4F6',
                    border: selected ? '2px solid #7C3AED' : '1px solid rgba(0,0,0,0.08)',
                    boxShadow: selected ? '0 8px 22px rgba(124,58,237,0.18)' : 'none',
                    opacity: playerEnabled ? 1 : 0.62,
                  }}
                  data-testid={`express-payout-method-${item.express_way_code}`}
                >
                  <PaymentMethodLogo method={item} accent="#7C3AED" accentDark="#5B21B6" />
                  <span className="min-w-0 flex-1">
                    <span className="display block font-extrabold text-slate-950">{item.title}</span>
                    <span className="mt-0.5 block text-[11px] font-semibold text-slate-500">{item.description}</span>
                  </span>
                  {selected && (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
                      <Check className="h-4 w-4" strokeWidth={4} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : renderMethodList('Pick a cashout method to continue.')}
    </div>
  );

  const renderWithdrawalDetailsStep = () => {
    const labels = cashoutMethodLabels(activeMethod || method);
    const expressMethod = cashoutSpeed === 'express' ? method : null;
    return (
      <div className="space-y-5" data-testid="wallet-step-withdrawal-details">
        {renderWithdrawalSummary()}
        {method && (
          <div className="space-y-3">
            {renderSelectedCashoutMethod()}
            {cashoutSpeed !== 'express' && (
              <ManualPaymentDetails method={method} accent={accent} accentDark={accentDark} showQr onPreviewQr={openManualQrPreview} />
            )}
            <div className="grid gap-3 sm:grid-cols-2" data-testid="cashout-payout-detail-boxes">
              <label className="rounded-2xl border border-violet-200 bg-violet-50/60 p-3">
                <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-[0.12em] text-violet-700">
                  {expressMethod ? 'Recipient name' : labels.nameLabel}
                </span>
                <Input
                  value={payoutName}
                  onChange={e => setPayoutName(e.target.value)}
                  placeholder={expressMethod ? 'Name on the receiving account' : labels.namePlaceholder}
                  className="h-12"
                  style={{ background: '#fff', border: '1px solid rgba(109,40,217,0.20)', color: '#0B0F0C' }}
                  data-testid="fs-payout-name"
                />
              </label>
              <label className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50/60 p-3">
                <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-[0.12em] text-fuchsia-700">
                  {expressMethod?.primaryLabel || labels.tagLabel}
                </span>
                <Input
                  value={walletAddress}
                  onChange={e => setWalletAddress(
                    expressMethod?.express_way_code === 'card'
                      ? formatCardNumberInput(e.target.value)
                      : e.target.value
                  )}
                  placeholder={expressMethod?.primaryPlaceholder || labels.tagPlaceholder}
                  inputMode={['card', 'ach'].includes(expressMethod?.express_way_code) ? 'numeric' : undefined}
                  maxLength={expressMethod?.express_way_code === 'card' ? 16 : undefined}
                  className="h-12"
                  style={{ background: '#fff', border: '1px solid rgba(192,38,211,0.20)', color: '#0B0F0C' }}
                  data-testid="fs-wallet-address"
                />
              </label>
              {expressMethod?.secondaryLabel && (
                <label className="rounded-2xl border border-cyan-200 bg-cyan-50/60 p-3 sm:col-span-2">
                  <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-[0.12em] text-cyan-800">
                    {expressMethod.secondaryLabel}
                  </span>
                  <Input
                    value={expressSecondary}
                    onChange={e => setExpressSecondary(
                      expressMethod.express_way_code === 'card'
                        ? formatCardExpiryInput(e.target.value)
                        : e.target.value
                    )}
                    placeholder={expressMethod.secondaryPlaceholder}
                    inputMode={['card', 'ach'].includes(expressMethod.express_way_code) ? 'numeric' : undefined}
                    maxLength={expressMethod.express_way_code === 'card' ? 7 : undefined}
                    className="h-12"
                    style={{ background: '#fff', border: '1px solid rgba(8,145,178,0.22)', color: '#0B0F0C' }}
                    data-testid="fs-express-secondary"
                  />
                </label>
              )}
            </div>
            <div
              className="rounded-2xl p-3 text-xs font-bold leading-snug flex items-start gap-2"
              style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.35)', color: '#991B1B' }}
              data-testid="cashout-detail-warning"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Enter your correct {expressMethod ? 'recipient name' : labels.nameLabel} and {expressMethod?.primaryLabel || labels.tagLabel}. If wrong cashout details are submitted, we are not responsible for failed or delayed payout.
              </span>
            </div>
            {cashoutSpeed !== 'express' && (
              <Input
                value={payoutDetails}
                onChange={e => setPayoutDetails(e.target.value)}
                placeholder="Additional details (optional)"
                className="h-12"
                style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderWithdrawalReviewStep = () => {
    const labels = cashoutMethodLabels(activeMethod || method);
    const expressWayCode = String(method?.express_way_code || '');
    const maskedDestination = (() => {
      const value = String(walletAddress || '').trim();
      const digits = value.replace(/\D/g, '');
      if (expressWayCode === 'card') return digits ? `Card ending ${digits.slice(-4)}` : '';
      if (expressWayCode === 'ach') return digits ? `Bank ending ${digits.slice(-4)}` : '';
      return value;
    })();
    return (
      <div className="space-y-5" data-testid="wallet-step-confirm">
        <div className="rounded-2xl p-5" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <p className="display font-extrabold text-lg mb-3" style={{ color: '#0B0F0C' }}>
            Review your cashout
          </p>
          <div className="space-y-2.5 text-sm">
            <Row label="Method" value={method?.title || '-'} />
            <Row label="Wallet deduction" value={moneyLabel(cashoutGrossAmount)} valueBold />
            {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
            {cashoutSpeed === 'express' && (
              <Row label={`Express fee (${expressFeePercent}%)`} value={moneyLabel(expressFeeAmount)} valueBold />
            )}
            <Row label="You receive" value={moneyLabel(cashoutPayoutAmount)} valueBold />
            {payoutName && <Row label={cashoutSpeed === 'express' ? 'Recipient name' : labels.nameLabel} value={payoutName} />}
            {walletAddress && (
              <Row
                label={cashoutSpeed === 'express' ? (method?.primaryLabel || 'Destination') : labels.tagLabel}
                value={cashoutSpeed === 'express' ? maskedDestination : walletAddress}
                mono
              />
            )}
            {payoutDetails && <Row label="Details" value={payoutDetails} />}
            <Row label="Current Balance" value={moneyLabel(balanceUsd)} />
          </div>
        </div>

        <div className="rounded-2xl p-4 text-sm" style={{ background: '#FEF3C7', border: '1px solid rgba(180,83,9,0.25)', color: '#92400E' }}>
          Once submitted, your cashout request will be processed by our team. You can track it in your transaction history.
        </div>
      </div>
    );
  };

  if (checkoutPanel) {
    const checkoutAsLink = Boolean(checkoutPanel.linkReady);
    const cryptoPayment = checkoutPanel.cryptoPayment || null;
    const directCryptoCheckout = Boolean(cryptoPayment?.pay_address || cryptoPayment?.pay_amount);
    const compactCheckout = checkoutAsLink || directCryptoCheckout;
    const cryptoCurrency = cryptoPayment?.pay_currency || cryptoPayment?.network || checkoutPanel.cryptoNetwork;
    const cryptoNetworkName = cryptoNetworkLabel(cryptoCurrency, checkoutPanel.cryptoNetwork);
    const cryptoExtraId = cryptoPayment?.payin_extra_id || cryptoPayment?.memo || cryptoPayment?.destination_tag;
    return (
      <div
        className={compactCheckout
          ? 'fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4'
          : 'fixed inset-0 z-[70] flex flex-col'}
        style={{
          background: compactCheckout ? 'rgba(11,15,12,0.62)' : '#FFFFFF',
          color: '#0B0F0C',
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}
        data-testid={compactCheckout ? 'payment-link-ready-modal' : 'embedded-checkout-panel'}
      >
        <div className={compactCheckout
          ? 'flex max-h-[calc(100dvh-1rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'
          : 'w-full h-full flex flex-col bg-white overflow-hidden'}>
        <div className={`${compactCheckout ? 'px-4 py-3 sm:px-5 sm:py-4' : 'px-4 sm:px-6 py-3'} flex shrink-0 items-center gap-3 border-b`} style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setCheckoutPanel(null)}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#F3F4F6', color: '#0B0F0C' }}
            aria-label="Back to amount selection"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--vl-brand-primary, #D4AF37)' }}>
              {directCryptoCheckout ? 'Crypto Deposit' : checkoutAsLink ? 'Website Deposit' : 'Secure Checkout'}
            </p>
            <p className="display font-extrabold truncate" style={{ color: '#0B0F0C' }}>
              {checkoutPanel.methodTitle}{checkoutPanel.cryptoNetwork ? ` · ${checkoutPanel.cryptoNetwork}` : ''} · ${Number(checkoutPanel.amount || 0).toFixed(2)}
            </p>
          </div>
          <button
            onClick={compactCheckout ? () => setCheckoutPanel(null) : close}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#F3F4F6', color: '#0B0F0C' }}
            aria-label={compactCheckout ? 'Close payment modal' : 'Close checkout'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          className={`${compactCheckout ? 'px-4 sm:px-5' : 'px-4'} shrink-0 py-2 text-center text-xs`}
          style={{
            background: checkoutPanel.failed ? '#FEF2F2' : checkoutPanel.verified ? '#F0FDF4' : 'rgba(255,217,61,0.16)',
            color: checkoutPanel.failed ? '#991B1B' : checkoutPanel.verified ? '#166534' : '#6B4800'
          }}
        >
          {checkoutPanel.verified
              ? 'Payment verified. Your wallet has been updated.'
            : checkoutPanel.failed
              ? 'Payment was not completed. You can go back and try again.'
              : directCryptoCheckout
                ? `Only send ${cryptoNetworkName} to this address. Your wallet updates after confirmation.`
              : checkoutAsLink
                ? 'Open the link to complete payment. Your wallet updates automatically when payment is verified.'
                : 'Keep this panel open after payment. Your wallet updates automatically when payment is verified.'}
        </div>
        {checkoutPanel.loading ? (
          <div className={compactCheckout ? 'min-h-0 flex-1 overflow-y-auto px-5 py-8 sm:px-6 sm:py-10' : 'flex-1 flex items-center justify-center px-6 py-10'}>
            <div className="w-full max-w-sm text-center">
              <style>{`
                @keyframes checkout-gold-spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes checkout-gold-pulse {
                  0%, 100% { opacity: 0.28; transform: scale(0.82); }
                  50% { opacity: 0.75; transform: scale(1.18); }
                }
                @keyframes checkout-gold-sweep {
                  0% { transform: rotate(0deg) translateX(34px) rotate(0deg); opacity: 0.15; }
                  50% { opacity: 1; }
                  100% { transform: rotate(360deg) translateX(34px) rotate(-360deg); opacity: 0.15; }
                }
              `}</style>
              <div className="relative w-28 h-28 mx-auto flex items-center justify-center" aria-label="Creating checkout">
                <span
                  className="absolute inset-2 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(255,217,61,0.42) 0%, rgba(255,217,61,0.14) 48%, rgba(255,217,61,0) 72%)',
                    boxShadow: '0 0 42px rgba(255,217,61,0.55)',
                    animation: 'checkout-gold-pulse 1.7s ease-in-out infinite',
                  }}
                  aria-hidden
                />
                <span
                  className="absolute w-24 h-24 rounded-full"
                  style={{
                    border: '2px solid rgba(212,175,55,0.24)',
                    borderTopColor: 'var(--vl-brand-primary, #D4AF37)',
                    borderRightColor: '#FFD93D',
                    filter: 'drop-shadow(0 0 12px rgba(255,217,61,0.7))',
                    animation: 'checkout-gold-spin 1.05s linear infinite',
                  }}
                  aria-hidden
                />
                <span
                  className="absolute w-4 h-4 rounded-full"
                  style={{
                    background: '#FFD93D',
                    boxShadow: '0 0 18px rgba(255,217,61,0.95), 0 0 34px rgba(212,175,55,0.75)',
                    animation: 'checkout-gold-sweep 1.05s linear infinite',
                  }}
                  aria-hidden
                />
                <div
                  className="relative w-16 h-16 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    background: brandLogoUrl ? '#FFFFFF' : 'linear-gradient(180deg, #FFF3A3 0%, #FFD93D 45%, var(--vl-brand-primary, #D4AF37) 100%)',
                    color: brandLogoUrl ? '#0B0F0C' : '#2B1E00',
                    border: '2px solid rgba(255,255,255,0.72)',
                    boxShadow: 'inset 0 2px 8px rgba(255,255,255,0.55), 0 14px 34px rgba(212,175,55,0.42), 0 0 0 5px rgba(255,217,61,0.18)',
                  }}
                >
                  {brandLogoUrl ? (
                    <SafeImage
                      src={brandLogoUrl}
                      alt={branding?.brand_name || 'Brand logo'}
                      className="w-full h-full object-cover"
                      data-testid="checkout-loader-brand-logo"
                      loading="eager"
                      fallback={<RefreshCw className="w-8 h-8 animate-spin" />}
                    />
                  ) : (
                    <RefreshCw
                      className="w-8 h-8"
                      strokeWidth={3}
                      style={{ animation: 'checkout-gold-spin 1.05s linear infinite' }}
                    />
                  )}
                  <span
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.42), transparent 36%)',
                      boxShadow: 'inset 0 0 16px rgba(43,30,0,0.18)',
                    }}
                    aria-hidden
                  />
                </div>
              </div>
              <p className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                {checkoutAsLink ? 'Creating payment link' : 'Creating secure checkout'}
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#64748B' }}>
                Hold on. We are generating your {checkoutPanel.methodTitle} {checkoutAsLink ? 'payment link' : 'payment screen'} now.
              </p>
            </div>
          </div>
        ) : checkoutPanel.verified || checkoutPanel.failed ? (
          <div className={compactCheckout ? 'min-h-0 flex-1 overflow-y-auto px-5 py-8 sm:px-6 sm:py-10' : 'flex-1 flex items-center justify-center px-6 py-10'}>
            <div className="w-full max-w-sm text-center">
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: checkoutPanel.verified ? '#DCFCE7' : '#FEE2E2',
                  color: checkoutPanel.verified ? '#16A34A' : '#DC2626'
                }}
              >
                {checkoutPanel.verified ? (
                  <CheckCircle2 className="w-11 h-11" strokeWidth={3} />
                ) : (
                  <XCircle className="w-11 h-11" strokeWidth={3} />
                )}
              </div>
              <p className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                {checkoutPanel.verified ? 'Payment verified' : 'Payment not completed'}
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#64748B' }}>
                {checkoutPanel.verified
                  ? `Your $${Number(checkoutPanel.amount || 0).toFixed(2)} ${checkoutPanel.methodTitle} deposit has been credited.`
                  : (checkoutPanel.errorMessage || 'No wallet credit was added for this attempt. Go back and choose an amount to try again.')}
              </p>
              {checkoutPanel.failed && (
                <button
                  onClick={() => setCheckoutPanel(null)}
                  className="mt-6 w-full h-12 rounded-xl display font-extrabold"
                  style={{ background: '#F3F4F6', color: '#0B0F0C' }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        ) : directCryptoCheckout ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 sm:py-6">
            <div className="text-center">
              <div
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                style={{ background: '#DCFCE7', color: '#16A34A' }}
              >
                <CheckCircle2 className="w-9 h-9" strokeWidth={3} />
              </div>
              <h2 className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                Payment details ready
              </h2>
            </div>

            <div className="rounded-2xl p-4" style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.35)' }}>
              <p className="text-xs uppercase tracking-wider font-extrabold" style={{ color: '#15803D' }}>
                Send exactly
              </p>
              <p className="mono text-2xl font-extrabold mt-1" style={{ color: '#0B0F0C' }}>
                {cryptoPayAmountLabel(cryptoPayment?.pay_amount, cryptoCurrency)}
              </p>
              <p className="text-xs mt-1 font-bold" style={{ color: '#4B5563' }}>
                Network: {cryptoNetworkName}
              </p>
            </div>

            {cryptoPayment?.pay_address && (
              <div className="rounded-2xl p-4" style={{ background: '#F8FAFC', border: '1px solid rgba(15,23,42,0.10)' }}>
                <p className="text-xs uppercase tracking-wider font-extrabold" style={{ color: '#64748B' }}>
                  Wallet address
                </p>
                <p className="mono text-sm mt-2 break-all" style={{ color: '#0B0F0C' }}>
                  {cryptoPayment.pay_address}
                </p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(cryptoPayment.pay_address, 'Address copied')}
                  className="mt-3 w-full h-11 rounded-xl display font-extrabold flex items-center justify-center gap-2"
                  style={{ background: '#0B0F0C', color: '#FFFFFF' }}
                >
                  <Copy className="w-4 h-4" /> Copy Address
                </button>
              </div>
            )}

            {cryptoExtraId && (
              <div className="rounded-2xl p-4" style={{ background: '#FFFBEB', border: '1px solid rgba(245,158,11,0.35)' }}>
                <p className="text-xs uppercase tracking-wider font-extrabold" style={{ color: '#92400E' }}>
                  Memo / extra id
                </p>
                <p className="mono text-sm mt-2 break-all" style={{ color: '#0B0F0C' }}>
                  {cryptoExtraId}
                </p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(cryptoExtraId, 'Memo copied')}
                  className="mt-3 w-full h-11 rounded-xl display font-extrabold flex items-center justify-center gap-2"
                  style={{ background: '#F59E0B', color: '#111827' }}
                >
                  <Copy className="w-4 h-4" /> Copy Memo
                </button>
              </div>
            )}

            <div className="rounded-2xl p-4 text-sm font-bold leading-snug" style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.35)', color: '#991B1B' }}>
              Do not send another coin or another network to this address. Wrong network payments may be lost.
            </div>

            {cryptoPayment?.payment_url && (
              <button
                type="button"
                onClick={() => openPaymentLinkExternally(cryptoPayment.payment_url)}
                className="w-full h-12 rounded-xl display font-extrabold"
                style={{ background: 'linear-gradient(180deg,#22C55E,#15803D)', color: '#FFFFFF', boxShadow: '0 8px 22px rgba(34,197,94,0.35)' }}
              >
                Open Payment Page
              </button>
            )}
          </div>
        ) : checkoutAsLink ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-6 sm:py-7">
            <div className="text-center">
              <div
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                style={{ background: '#DCFCE7', color: '#16A34A' }}
              >
                <CheckCircle2 className="w-9 h-9" strokeWidth={3} />
              </div>
              <h2 className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                Your link is ready
              </h2>
            </div>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => openPaymentLinkExternally(checkoutPanel.originalUrl || checkoutPanel.url)}
                className="w-full h-12 rounded-xl display font-extrabold"
                style={{ background: 'linear-gradient(180deg,#22C55E,#15803D)', color: '#FFFFFF', boxShadow: '0 8px 22px rgba(34,197,94,0.35)' }}
                data-testid="payment-link-proceed"
              >
                Proceed to Pay
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            <iframe
              title="Secure payment checkout"
              src={checkoutPanel.url}
              className="flex-1 w-full border-0"
              allow={checkoutPanel.applePay ? 'payment; publickey-credentials-get; clipboard-read; clipboard-write' : 'payment; clipboard-read; clipboard-write'}
              allowPaymentRequest
              {...(checkoutPanel.applePay ? {} : {
                sandbox: 'allow-forms allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-storage-access-by-user-activation'
              })}
            />
          </div>
        )}
        <div className={`${compactCheckout ? 'px-4 py-3 sm:px-5 sm:py-4' : 'px-4 sm:px-6 py-3'} shrink-0 border-t`} style={{ borderColor: 'rgba(0,0,0,0.08)', background: '#FFFFFF' }}>
          <button
            onClick={close}
            className="w-full h-12 rounded-xl display font-extrabold flex items-center justify-center gap-2"
            style={{ background: '#0B0F0C', color: '#FFFFFF' }}
          >
            <ChevronLeft className="w-4 h-4" /> Return to Home
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {cashoutDetailsWarningOpen && mode === 'withdrawal' && step === 3 && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cashout-details-warning-title"
          data-testid="cashout-details-warning-modal"
        >
          <div className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-fuchsia-400/40 bg-slate-950 p-5 pt-16 text-white shadow-[0_24px_80px_rgba(109,40,217,0.50)]">
            <button
              type="button"
              onClick={() => setCashoutDetailsWarningOpen(false)}
              className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 transition hover:bg-white/20 active:scale-95"
              aria-label="Close payout details warning"
              data-testid="close-cashout-details-warning"
            >
              <X className="h-5 w-5" strokeWidth={3} />
            </button>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-500 to-violet-600 shadow-[0_0_28px_rgba(217,70,239,0.55)]">
              <AlertTriangle className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <div className="mt-4 text-center">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-fuchsia-300">One last step</p>
              <h2 id="cashout-details-warning-title" className="display mt-1 text-2xl font-black">Double-check your details</h2>
              <p className="mt-3 text-sm font-semibold leading-relaxed text-slate-300">
                Cashouts sent to the wrong account may not be refundable. Verify the name and account number or tag before continuing.
              </p>
            </div>
            <Button
              type="button"
              onClick={() => setCashoutDetailsWarningOpen(false)}
              className="mt-5 h-12 w-full rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 font-extrabold text-white"
              data-testid="review-cashout-details"
            >
              Review my details
            </Button>
          </div>
        </div>
      )}

      <button
        onClick={requestClose}
        className="fixed top-4 right-4 z-[70] w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105"
        style={{
          background: 'rgba(0,0,0,0.55)',
          border: '2px solid rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)'
        }}
        data-testid="wallet-close"
        aria-label="Close"
      >
        <X className="w-5 h-5 text-white" strokeWidth={3} />
      </button>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10 sm:py-12">
          <Stepper step={step} accent={accent} accentDark={accentDark} labels={stepperLabels} />

          {!(mode === 'withdrawal' && cashoutSpeed === 'express') && (
            <div className="flex justify-start mt-8 mb-5">
              <div
                className="flex max-w-full flex-wrap items-center gap-2 rounded-xl px-3 py-2 min-[360px]:px-4"
                style={{
                  background: mode === 'deposit' ? '#F0FDF4' : '#FFF7ED',
                  border: `1px solid ${accent}55`
                }}
                data-testid="wallet-current-balance"
              >
                <Wallet className="h-4 w-4 shrink-0" style={{ color: accentDark }} />
                <span className="text-xs uppercase tracking-wider font-bold" style={{ color: accentDark }}>{mode === 'withdrawal' ? 'Available Balance:' : 'Current Balance:'}</span>
                <span className="display min-w-0 break-all font-extrabold mono" style={{ color: accentDark }}>${balanceUsd.toFixed(2)}</span>
              </div>
            </div>
          )}

          {mode === 'withdrawal' && cashoutSpeed !== 'express' && (
            <div
              className="mb-5 flex items-center gap-3 rounded-2xl border px-4 py-3"
              style={{
                background: cashoutSpeed === 'express' ? '#F5F3FF' : '#FFF7ED',
                borderColor: cashoutSpeed === 'express' ? 'rgba(124,58,237,0.28)' : 'rgba(249,115,22,0.28)',
              }}
              data-testid="wallet-cashout-speed"
            >
              {cashoutSpeed === 'express'
                ? <Zap className="h-5 w-5 shrink-0 text-violet-600" />
                : <Clock3 className="h-5 w-5 shrink-0 text-orange-600" />}
              <div>
                <p className="text-xs font-extrabold text-slate-950">
                  {cashoutSpeed === 'express' ? 'Express Cashout' : 'Normal Cashout'}
                </p>
                <p className="mt-0.5 text-[11px] font-semibold text-slate-500">
                  {cashoutSpeed === 'express' ? 'Usually completes within minutes.' : 'Usually takes 5–10 minutes.'}
                </p>
              </div>
            </div>
          )}

          {mode === 'withdrawal' && cashoutSpeed === 'express' && (
            <section
              className="mb-5 mt-8 space-y-3"
              data-testid="express-cashout-details"
            >
              <div className="flex items-center gap-3 rounded-2xl bg-slate-950 px-3 py-2.5 text-white shadow-lg">
                <button
                  type="button"
                  onClick={onBackToCashoutChoice}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 transition hover:bg-white/20 active:scale-95"
                  aria-label="Back to cashout options"
                  data-testid="express-cashout-back"
                >
                  <ChevronLeft className="h-5 w-5" strokeWidth={3} />
                </button>
                <div className="min-w-0">
                  <p className="text-[9px] font-extrabold uppercase tracking-[0.14em] text-violet-200">Available balance</p>
                  <p className="display truncate text-xl font-black mono">{moneyLabel(balanceUsd)}</p>
                </div>
              </div>
              {step !== 3 && (
                <img
                  src="/assets/express-cashout-payment-options.png"
                  alt="Express Checkout accepts supported cards, Cash App, Chime, Venmo and bank payouts"
                  className="block h-auto w-full rounded-2xl border border-cyan-300/30 object-cover shadow-[0_16px_38px_rgba(8,145,178,0.18)]"
                  loading="eager"
                  data-testid="express-cashout-payment-artwork"
                />
              )}
            </section>
          )}

          {mode === 'withdrawal' ? (
            step === 1
              ? renderWithdrawalAmountStep()
              : step === 2
                ? renderWithdrawalMethodStep()
                : step === 3
                  ? renderWithdrawalDetailsStep()
                  : renderWithdrawalReviewStep()
          ) : (
            step === 1 ? renderMethodList() : renderDepositAmountStep()
          )}
        </div>
      </div>

      <div
        className="shrink-0 border-t"
        style={{
          background: '#FFFFFF',
          borderColor: 'rgba(0,0,0,0.08)',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.05)'
        }}
        data-testid="wallet-footer"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-4 space-y-2">
          {step === 1 && (
            <Button
              onClick={() => {
                if (mode === 'withdrawal') {
                  if (validateWithdrawalAmountStep()) setStep(2);
                  return;
                }
                if (method) setStep(2);
                else toast.error('Select a payment method');
              }}
              disabled={mode === 'withdrawal' ? withdrawalAmountStepLocked : !method}
              className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                color: '#fff',
                boxShadow: (mode === 'withdrawal' ? !withdrawalAmountStepLocked : method) ? `0 8px 22px ${accent}66` : 'none'
              }}
              data-testid="wallet-next-btn"
            >
              Next →
            </Button>
          )}

          {step === 2 && mode === 'deposit' && (
            <>
              <Button
                onClick={() => {
                  if (isSupportContactMethod(activeMethod || method)) {
                    openSupportForLoad();
                    return;
                  }
                  submit();
                }}
                disabled={
                  processing
                  || depositRulesLocked
                  || (
                    !isSupportContactMethod(activeMethod || method)
                    && (
                      !amount
                      || depositAmountBelowMinimum(activeMethod || method)
                      || (!isApiGatewayMethod(activeMethod || method) && !proofFile)
                    )
                  )
                }
                className="w-full h-14 display font-bold text-base rounded-xl"
                style={{
                  background: 'linear-gradient(180deg,#22C55E,#15803D)',
                  color: '#fff',
                  boxShadow: '0 8px 22px rgba(34,197,94,0.4)'
                }}
                data-testid="fs-wallet-submit"
              >
                {processing && isApiGatewayMethod(activeMethod || method) ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="spinner w-5 h-5" />
                    <span>Creating payment link…</span>
                  </div>
                ) : (
                  isSupportContactMethod(activeMethod || method)
                    ? 'Open Support →'
                    : isApiGatewayMethod(activeMethod || method)
                    ? 'Create Payment Link →'
                    : 'Confirm Purchase'
                )}
              </Button>
              <button
                onClick={close}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to Home
              </button>
            </>
          )}

          {step === 2 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => {
                  if (!method) { toast.error('Select a cashout method'); return; }
                  setStep(3);
                }}
                disabled={!method}
                className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
                style={{
                  background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                  color: '#fff',
                  boxShadow: method ? `0 8px 22px ${accent}66` : 'none'
                }}
                data-testid="wallet-next-step3-btn"
              >
                Next →
              </Button>
              <button
                onClick={() => setStep(1)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to amount
              </button>
            </>
          )}

          {step === 3 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => {
                  const labels = cashoutMethodLabels(activeMethod || method);
                  if (!String(payoutName || '').trim() || !String(walletAddress || '').trim()) {
                    toast.error(cashoutSpeed === 'express'
                      ? `Enter recipient name and ${method?.primaryLabel || 'payout details'}`
                      : `Enter ${labels.nameLabel} and ${labels.tagLabel}`);
                    return;
                  }
                  if (cashoutSpeed === 'express' && expressDestinationError) {
                    toast.error(expressDestinationError);
                    return;
                  }
                  setStep(4);
                }}
                disabled={!withdrawalDetailsComplete}
                className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
                style={{
                  background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                  color: '#fff',
                  boxShadow: withdrawalDetailsComplete ? `0 8px 22px ${accent}66` : 'none'
                }}
                data-testid="wallet-next-step4-btn"
              >
                Next →
              </Button>
              <button
                onClick={() => setStep(2)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to cashout method
              </button>
            </>
          )}

          {step === 4 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => submit()}
                disabled={processing}
                className="w-full h-14 display font-bold text-base rounded-xl"
                style={{
                  background: 'linear-gradient(180deg,#F97316,#C2410C)',
                  color: '#fff',
                  boxShadow: '0 8px 22px rgba(249,115,22,0.4)'
                }}
                data-testid="fs-wallet-submit"
              >
                {processing ? <div className="spinner w-5 h-5" /> : 'Confirm Cashout'}
              </Button>
              <button
                onClick={() => setStep(3)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to transaction details
              </button>
            </>
          )}
        </div>
      </div>

      {manualQrPreview && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center overflow-y-auto bg-black/75 p-2 sm:p-4"
          onClick={() => setManualQrPreview(null)}
          data-testid="manual-payment-qr-preview"
        >
          <div
            className="relative max-h-[calc(100dvh-1rem)] w-full max-w-[420px] overflow-y-auto rounded-3xl bg-white p-3 shadow-2xl sm:p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setManualQrPreview(null)}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full text-white"
              style={{ background: 'rgba(11,15,12,0.82)', boxShadow: '0 8px 22px rgba(0,0,0,0.28)' }}
              aria-label="Close payment QR"
              data-testid="close-manual-payment-qr-preview"
            >
              <X className="h-5 w-5" strokeWidth={3} />
            </button>
            <p className="display mb-3 pr-12 text-base font-extrabold" style={{ color: '#0B0F0C' }}>
              {manualQrPreview.title}
            </p>
            <SafeImage
              src={manualQrPreview.url}
              alt={`${manualQrPreview.title} enlarged`}
              className="max-h-[calc(100dvh-6rem)] w-full rounded-2xl bg-white object-contain"
              style={{ border: '1px solid rgba(0,0,0,0.08)' }}
              fallback={null}
            />
          </div>
        </div>
      )}

      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent
          overlayClassName="!bg-black/65 backdrop-blur-md !z-[100]"
          className="max-w-md border-0 p-7 sm:p-10 rounded-3xl !z-[101]"
          style={{
            background: '#FFFFFF',
            color: '#0B0F0C',
            boxShadow: '0 30px 70px rgba(0,0,0,0.45)'
          }}
        >
          <DialogHeader>
            <DialogTitle
              className="display font-extrabold text-center"
              style={{
                color: '#0B0F0C',
                fontSize: '28px',
                lineHeight: 1.15,
                letterSpacing: '-0.01em'
              }}
            >
              Are you sure you want to leave?
            </DialogTitle>
          </DialogHeader>
          <p
            className="text-center mt-4"
            style={{ color: '#1F2937', fontSize: '17px', fontWeight: 500 }}
          >
            Your transaction will be canceled if you proceed.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-7">
            <button
              onClick={() => { setConfirmCloseOpen(false); close(); }}
              className="display font-extrabold rounded-2xl text-white text-sm sm:text-base px-3 py-4 transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: '#E11D2E',
                boxShadow: '0 10px 24px rgba(225,29,46,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
              }}
              data-testid="confirm-leave-yes-btn"
            >
              Yes, I want to go out
            </button>
            <button
              onClick={() => setConfirmCloseOpen(false)}
              className="display font-extrabold rounded-2xl text-white text-sm sm:text-base px-3 py-4 transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: '#15A036',
                boxShadow: '0 10px 24px rgba(21,160,54,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
              }}
              data-testid="confirm-leave-continue-btn"
            >
              I want to continue
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
